import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ArrowUp,
  Users,
  Utensils,
  Accessibility,
  CheckCircle2,
  Zap,
  Bell,
  Settings,
  Eye,
  EyeOff,
  Edit3,
  Save,
  X,
  Plus,
  Trash2,
  RefreshCw,
  ChevronRight,
  Info,
  ExternalLink,
  AlertCircle,
  MoreVertical,
  CalendarDays,
  Archive as ArchiveIcon,
  ListChecks,
  TrendingUp,
  Heart,
  Sparkles,
  Package,
  BarChart3,
  Loader2
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { getAllCategories, getCategoryPreset } from '../../lib/categoryConfig';
import AdminOnlyNote from './AdminOnlyNote';
import PreferenceFieldsEditor from './PreferenceFieldsEditor';
import MultiSelectChips from './MultiSelectChips';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pilot categories sourced from centralized config
const PILOT_CATEGORIES = getAllCategories().map(c => ({ id: c.key, name: c.name, color: c.iconColor, bgColor: c.bgColor, icon: c.icon }));

// Default template configuration
const DEFAULT_TEMPLATE = {
  // Kanban lanes the host actually sees (Iter 9.40 Slice C⁵).
  // Replaces legacy stat_cards (Total Attending / RSVPs / Checked In / Dietary / Accessibility).
  stat_cards: {
    invites_sent:       { enabled: true, label: 'Invites Sent' },
    preferences_shared: { enabled: true, label: 'Preferences Shared' },
    complete:           { enabled: true, label: 'Complete' },
    archive:            { enabled: true, label: 'Archive' },
  },
  // Collect options
  collect: {
    dietary: false,
    accessibility: false,
    interests: true,
    vibe: false,
    transportation: false,
  },
  // Quick actions
  quick_actions: {
    share_with_instructor: false,
    share_music_preferences: false,
    share_dietary_summary: true,
    share_accessibility_summary: true,
    invite_to_list: true,
  },
  // Display toggles
  display: {
    show_tech_stack: true,
    show_quick_actions: true,
    show_partners: true,
    show_hosted_by: true,
    nfc_kiosk_enabled: false,
  },
  // Welcome message
  welcome_message: '',
  // Team labels
  team_labels: ['host', 'team', 'speaker', 'sponsor', 'vip', 'vendor', 'partner', 'attendee'],
};

/**
 * HostDashboardTemplateEditor (Iter 9.36)
 *
 * Tabs mirror the REAL host dashboard exactly:
 *   Pre-host (client flow): Branding | Envelope | Welcome | Intake | Confirmation | Pilot Onboarding
 *   Host-facing (mirrors HostDashboardV2 1:1):
 *     Dashboard | Clients (label is category-aware: Guests for events, Learners for instructor) | Insights | Settings
 *
 * Now also accepts `categoryProp` so the parent (Product sidebar) can render
 * this inline and keep the sidebar persistent without a route navigation.
 */

// Iter 9.40 Slice I - main host dashboard tab is "Clients" (with Directory
// and Appointments as inner sub-tabs). Per-category synonyms:
//   makeup / hair / nails / lashes / wellness / dispensary / culinary → "Clients"
//   events                                                            → "Guests"
//   instructors                                                        → "Learners"
function clientsLabelFor(catKey) {
  const k = (catKey || "").toLowerCase();
  if (k === "events" || k === "event") return "Guests";
  if (k === "instructor" || k === "instructors") return "Learners";
  return "Clients";
}

export default function HostDashboardTemplateEditor({ categoryProp, embedded = false }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(() => {
    if (categoryProp) return categoryProp;
    // Iter 9.34 - read ?cat= query string so HOST DASHBOARDS sidebar can deep-link
    if (typeof window !== "undefined") {
      const p = new URLSearchParams(window.location.search);
      return p.get("cat") || "events";
    }
    return "events";
  });

  // Iter 9.36 - when parent passes a different categoryProp, sync internal state
  useEffect(() => {
    if (categoryProp && categoryProp !== selectedCategory) {
      setSelectedCategory(categoryProp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryProp]);
  const [templates, setTemplates] = useState({});
  const [editMode, setEditMode] = useState(true);
  const [activeSection, setActiveSection] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  // Iter 9.34: live category doc (branding/envelope/welcome/intake/confirmation/pilot panels)
  const [categoryDoc, setCategoryDoc] = useState(null);
  const [panelDrafts, setPanelDrafts] = useState({});
  
  // Current template being edited
  const currentTemplate = templates[selectedCategory] || { ...DEFAULT_TEMPLATE };
  
  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };
  
  useEffect(() => {
    loadTemplates();
  }, []);

  // Iter 9.34: load full category doc whenever the selection changes
  useEffect(() => {
    const loadCategory = async () => {
      try {
        const r = await fetch(`${API}/admin/categories/${selectedCategory}/full`, {
          headers: getAuthHeaders(),
        });
        if (r.ok) {
          const d = await r.json();
          setCategoryDoc(d);
          setPanelDrafts({
            branding: {
              label: d.label || "",
              icon_url: d.icon_url || "",
              light_hex: d.light_hex || "",
              dark_hex: d.dark_hex || "",
              display_font: d.display_font || "",
              hero_title: d.hero_title || "",
              hero_subtitle: d.hero_subtitle || "",
              sales_page_slug: d.sales_page_slug || "",
              plan_verb: d.plan_verb || "",
              client_noun: d.client_noun || "",
              instructor_eligible: !!d.instructor_eligible,
            },
            envelope: d.envelope || { cover_image: "", copy: "" },
            welcome: d.welcome || { copy: "", slot_picker_enabled: true },
            intake: d.intake || { schema: d.preference_schema || [] },
            confirmation: d.confirmation || { pre: "", during: "", post: "" },
            pilot_onboarding: d.pilot_onboarding || { copy_overrides: {} },
            // Iter 9.36 - host-facing tab configurations
            clients: d.clients || {},
            insights: d.insights || {},
            host_settings: d.host_settings || {},
            // Iter 9.39a - sub-panels surfaced as editable lists in the visual editor
            preference_fields: d.preference_fields || d.preference_schema || [],
            inspo_default_folders: d.inspo_default_folders || d.inspo_folders || [],
            linked_occasions: d.linked_occasions || [],
            linked_services: d.linked_services || [],
            linked_products: d.linked_products || (d.master_products || []),
            experience_type: {
              experience_type_label: d.experience_type_label || "",
              experience_verb: d.experience_verb || "",
            },
            integrations: d.integrations || {},
          });
        }
      } catch (e) {
        console.error("loadCategory error", e);
      }
    };
    loadCategory();
  }, [selectedCategory]);

  const savePanel = async (panel) => {
    const draft = panelDrafts[panel];
    if (!draft) return;
    try {
      const r = await fetch(`${API}/admin/categories/${selectedCategory}/panel/${panel}`, {
        method: "PUT",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });
      if (r.ok) {
        const d = await r.json();
        setCategoryDoc(d.category);
        toast.success(`${panel.replace("_", " ")} saved`);
      } else {
        toast.error("Save failed");
      }
    } catch (e) {
      toast.error("Save error");
    }
  };

  const updateDraft = (panel, key, value) => {
    setPanelDrafts((p) => ({ ...p, [panel]: { ...(p[panel] || {}), [key]: value } }));
  };
  
  const loadTemplates = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/host-templates`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data || {});
      }
    } catch (err) {
      console.error('Error loading templates:', err);
    }
    setLoading(false);
  };
  
  const saveTemplate = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/host-templates/${selectedCategory}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(currentTemplate)
      });
      if (res.ok) {
        toast.success(`Template saved for ${PILOT_CATEGORIES.find(c => c.id === selectedCategory)?.name}`);
        loadTemplates();
      }
    } catch (err) {
      toast.error('Failed to save template');
    }
    setSaving(false);
  };
  
  const updateTemplate = (updates) => {
    setTemplates(prev => ({
      ...prev,
      [selectedCategory]: { ...currentTemplate, ...updates }
    }));
  };
  
  const updateStatCard = (cardId, updates) => {
    const newStatCards = { ...currentTemplate.stat_cards, [cardId]: { ...currentTemplate.stat_cards[cardId], ...updates } };
    updateTemplate({ stat_cards: newStatCards });
  };
  
  const updateCollect = (key, value) => {
    updateTemplate({ collect: { ...currentTemplate.collect, [key]: value } });
  };
  
  const updateDisplay = (key, value) => {
    updateTemplate({ display: { ...currentTemplate.display, [key]: value } });
  };
  
  const updateQuickAction = (key, value) => {
    updateTemplate({ quick_actions: { ...currentTemplate.quick_actions, [key]: value } });
  };
  
  // Editable wrapper component - adds edit overlay to any section
  const EditableSection = ({ id, label, children, onEdit }) => {
    const isActive = activeSection === id;
    
    return (
      <div 
        className={`relative group ${editMode ? 'cursor-pointer' : ''}`}
        onClick={() => editMode && setActiveSection(isActive ? null : id)}
      >
        {children}
        
        {/* Edit overlay - shows on hover in edit mode */}
        {editMode && (
          <div className={`absolute inset-0 border-2 rounded-xl transition-all pointer-events-none ${
            isActive ? 'border-purple-500 bg-purple-500/10' : 'border-transparent group-hover:border-purple-300 group-hover:bg-purple-50/50'
          }`}>
            <div className={`absolute top-2 right-2 ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity pointer-events-auto`}>
              <Badge className="bg-purple-600 text-white cursor-pointer" onClick={(e) => { e.stopPropagation(); setActiveSection(id); setShowEditDialog(true); }}>
                <Edit3 className="w-3 h-3 mr-1" /> Edit {label}
              </Badge>
            </div>
          </div>
        )}
      </div>
    );
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }
  
  return (
    <div className={embedded ? "bg-white" : "min-h-screen bg-gray-100"} data-testid="host-dashboard-template-editor">
      {/* Admin Control Bar */}
      <div className={`${embedded ? "" : "sticky top-0"} z-50 bg-white border-b shadow-sm`}>
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-4">
            <h1 className="text-lg font-semibold">Host Dashboard Template Editor</h1>
            <Badge variant="outline" className="text-purple-600">ADMIN MODE</Badge>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Category context comes from sidebar Host Dashboards selection - internal dropdown removed (Slice B) */}
            <Badge variant="outline" className="text-blue-700 border-blue-200 bg-blue-50" data-testid="tpl-category-context">
              {PILOT_CATEGORIES.find(c => c.id === selectedCategory)?.name || selectedCategory}
            </Badge>

            <Button onClick={saveTemplate} disabled={saving} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="tpl-save-template">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Template'}
            </Button>
          </div>
        </div>
        
        {/* Edit mode indicator */}
        {editMode && (
          <div className="bg-purple-50 border-t border-purple-200 px-4 py-2">
            <p className="text-sm text-purple-700">
              <Edit3 className="w-4 h-4 inline mr-2" />
              Click on any section to edit. Changes apply to all <strong>{PILOT_CATEGORIES.find(c => c.id === selectedCategory)?.name}</strong> dashboards.
            </p>
          </div>
        )}
      </div>
      
      {/* MIRRORED HOST DASHBOARD - Exact replica */}
      <div className={embedded ? "p-4 md:p-6" : "max-w-7xl mx-auto p-4 md:p-6"}>
        {/* Header - Like the actual host dashboard */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            {!embedded && (
              <Button variant="ghost" size="sm" className="gap-2">
                <ArrowLeft className="h-4 w-4" /> Back
              </Button>
            )}
            <div>
              <h2 className="text-xl font-bold">Template: {PILOT_CATEGORIES.find(c => c.id === selectedCategory)?.name}</h2>
              <p className="text-sm text-gray-500">Configure how host dashboards appear for this category</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Inner duplicate Preview/bell removed (Slice B) - single Save button up top */}
          </div>
        </div>

        {/* Host header preview - generic default template (no real host name).
            Iter 9.40 Slice C⁵: removed hardcoded "Amayah" - this IS the default that
            syncs to all host dashboards for the selected category, not a specific host preview. */}
        <div className="flex items-center gap-3 mb-4 px-1" data-testid="tpl-host-header-preview">
          <div className="w-12 h-12 rounded-md bg-black text-white flex items-center justify-center text-lg font-bold">
            {(PILOT_CATEGORIES.find(c => c.id === selectedCategory)?.name || "H")[0]}
          </div>
          <div>
            <p className="text-base font-semibold capitalize">{PILOT_CATEGORIES.find(c => c.id === selectedCategory)?.name} Host Dashboard</p>
            <p className="text-xs text-stone-500">Default template · synced to every {PILOT_CATEGORIES.find(c => c.id === selectedCategory)?.name} host</p>
          </div>
        </div>
        
        {/* Iter 9.40 Slice B - Branding & Experiences tab removed per user spec.
            All branding/envelope/welcome/intake/confirmation/product-picker/services
            now live INSIDE Host Dashboard → Settings, synced from master DBs. */}
        <Tabs defaultValue="host_dash" className="mb-3">
          <TabsList className="hidden">
            <TabsTrigger value="host_dash">Host Dashboard</TabsTrigger>
          </TabsList>

          {/* TOP TAB 1 (Branding & Experiences) - INTENTIONALLY HIDDEN */}
          {false && (
          <TabsContent value="brand_exp">
            <Tabs defaultValue="branding">
              <TabsList className="mb-4 flex-wrap h-auto">
                <TabsTrigger value="branding" data-testid="tpl-tab-branding">Branding</TabsTrigger>
                <TabsTrigger value="preferences-cat" data-testid="tpl-tab-preferences-cat">Preferences</TabsTrigger>
                <TabsTrigger value="inspo-cat" data-testid="tpl-tab-inspo-cat">Inspo defaults</TabsTrigger>
                <TabsTrigger value="occasions" data-testid="tpl-tab-occasions">Occasions</TabsTrigger>
                <TabsTrigger value="experience-types" data-testid="tpl-tab-experience-types">Experience type</TabsTrigger>
                {/* Pilot onboarding tab removed Iter 9.39b - pilot management now lives in Product → Portals → Pilot. */}
              </TabsList>

              {/* Preferences for category - Iter 9.39c: visible explanation of single-source sync */}
              <TabsContent value="preferences-cat">
                <Card><CardContent className="p-6 space-y-3" data-testid="tpl-preferences-cat-panel">
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-purple-50 border border-purple-100">
                    <Info className="w-4 h-4 text-purple-600 mt-0.5 shrink-0" />
                    <div className="text-xs text-purple-900">
                      <div className="font-semibold mb-0.5">Single source of truth</div>
                      These preferences live at <code className="text-[10px]">db.categories.{selectedCategory}.preference_fields</code>.
                      They auto-sync to: the /me preference card, this host dashboard's intake, the host dashboard's analytics labels,
                      and the Master Intakes <code className="text-[10px]">{`{${selectedCategory}, subscriber_host}`}</code> schema.
                      Editing here updates every consumer.
                      {" "}<a href="/admin/product/master-intakes" className="underline">Open Master Intakes</a>.
                    </div>
                  </div>
                  <h3 className="font-semibold text-sm">Preferences for {selectedCategory}</h3>
                  <PreferenceFieldsEditor
                    value={panelDrafts.preference_fields || []}
                    onChange={(next) => setPanelDrafts(d => ({ ...d, preference_fields: next }))}
                    testidPrefix="preferences-cat-fields"
                  />
                  <Button onClick={() => savePanel("preference_fields")} className="bg-purple-600" data-testid="save-preferences-cat"><Save className="w-4 h-4 mr-2" />Save preferences</Button>
                </CardContent></Card>
              </TabsContent>

              {/* Inspo defaults */}
              <TabsContent value="inspo-cat">
                <Card><CardContent className="p-6 space-y-3" data-testid="tpl-inspo-cat-panel">
                  <h3 className="font-semibold text-sm">Inspo folder defaults for {selectedCategory}</h3>
                  <p className="text-xs text-gray-500">Default folder structure auto-created when a host activates this category. Edit the labels.</p>
                  <FieldTextarea
                    label="Default folders (one per line)"
                    rows={6}
                    value={(panelDrafts.inspo_default_folders || []).join("\n")}
                    onChange={v => setPanelDrafts(d => ({ ...d, inspo_default_folders: v.split("\n").map(s => s.trim()).filter(Boolean) }))}
                    testid="inspo-default-folders"
                  />
                  <Button onClick={() => savePanel("inspo_default_folders")} className="bg-purple-600" data-testid="save-inspo-cat"><Save className="w-4 h-4 mr-2" />Save inspo defaults</Button>
                </CardContent></Card>
              </TabsContent>

              {/* Occasions */}
              <TabsContent value="occasions">
                <Card><CardContent className="p-6 space-y-3" data-testid="tpl-occasions-panel">
                  <h3 className="font-semibold text-sm">Occasions linked to {selectedCategory}</h3>
                  <p className="text-xs text-gray-500">Master list at <a href="/admin/product/master-occasions" className="text-purple-600 underline">/admin/product/master-occasions</a>. Add tags that apply to this category.</p>
                  <MultiSelectChips
                    value={panelDrafts.linked_occasions || []}
                    onChange={(next) => setPanelDrafts(d => ({ ...d, linked_occasions: next }))}
                    placeholder="Add occasion (e.g. wedding, prom)"
                    testid="occasions-linked"
                  />
                  <Button onClick={() => savePanel("linked_occasions")} className="bg-purple-600" data-testid="save-occasions"><Save className="w-4 h-4 mr-2" />Save occasions</Button>
                </CardContent></Card>
              </TabsContent>

              {/* Experience type / name */}
              <TabsContent value="experience-types">
                <Card><CardContent className="p-6 space-y-3" data-testid="tpl-experience-types-panel">
                  <h3 className="font-semibold text-sm">Experience type for {selectedCategory}</h3>
                  <p className="text-xs text-gray-500">e.g. Retail =&gt; "Style experience", Culinary =&gt; "Food", Hair =&gt; "Hair appointment".</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <FieldText
                      label="Experience type label"
                      value={panelDrafts.experience_type?.experience_type_label}
                      onChange={v => updateDraft("experience_type", "experience_type_label", v)}
                      testid="experience-type-label"
                    />
                    <FieldText
                      label="Experience verb (e.g. 'Plan a service')"
                      value={panelDrafts.experience_type?.experience_verb}
                      onChange={v => updateDraft("experience_type", "experience_verb", v)}
                      testid="experience-verb"
                    />
                  </div>
                  <Button onClick={() => savePanel("experience_type")} className="bg-purple-600" data-testid="save-experience-type"><Save className="w-4 h-4 mr-2" />Save experience type</Button>
                </CardContent></Card>
              </TabsContent>
              {/* end of new sub-tabs - original branding + pilot panels follow */}

          {/* ─── Iter 9.34: Branding panel ────────────────────────────────── */}
          <TabsContent value="branding">
            <Card>
              <CardContent className="p-6 space-y-4" data-testid="tpl-branding-panel">
                <h3 className="font-semibold text-sm">Branding (db.categories.{selectedCategory})</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <FieldText label="Label" value={panelDrafts.branding?.label} onChange={v => updateDraft("branding", "label", v)} testid="branding-label" />
                  <FieldText label="Display font" value={panelDrafts.branding?.display_font} onChange={v => updateDraft("branding", "display_font", v)} testid="branding-font" />
                  <FieldHex label="Light hex" value={panelDrafts.branding?.light_hex} onChange={v => updateDraft("branding", "light_hex", v)} testid="branding-light-hex" />
                  <FieldHex label="Dark hex" value={panelDrafts.branding?.dark_hex} onChange={v => updateDraft("branding", "dark_hex", v)} testid="branding-dark-hex" />
                  <FieldImage label="Icon" value={panelDrafts.branding?.icon_url} onChange={v => updateDraft("branding", "icon_url", v)} testid="branding-icon" />
                  <FieldText label="Sales page slug" value={panelDrafts.branding?.sales_page_slug} onChange={v => updateDraft("branding", "sales_page_slug", v)} testid="branding-slug" />
                  <FieldText label="Hero title" value={panelDrafts.branding?.hero_title} onChange={v => updateDraft("branding", "hero_title", v)} testid="branding-hero-title" />
                  <FieldText label="Hero subtitle" value={panelDrafts.branding?.hero_subtitle} onChange={v => updateDraft("branding", "hero_subtitle", v)} testid="branding-hero-sub" />
                  <FieldText label="Plan verb (e.g. 'Plan a service')" value={panelDrafts.branding?.plan_verb} onChange={v => updateDraft("branding", "plan_verb", v)} testid="branding-plan-verb" />
                  <FieldText label="Client noun (e.g. 'Client', 'Guest')" value={panelDrafts.branding?.client_noun} onChange={v => updateDraft("branding", "client_noun", v)} testid="branding-client-noun" />
                  <FieldBool label="Instructor add-on eligible" value={!!panelDrafts.branding?.instructor_eligible} onChange={v => updateDraft("branding", "instructor_eligible", v)} testid="branding-instructor" />
                </div>
                <Button onClick={() => savePanel("branding")} className="bg-purple-600 hover:bg-purple-700" data-testid="save-branding">
                  <Save className="w-4 h-4 mr-2" />Save Branding
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="envelope">
            <Card>
              <CardContent className="p-6 space-y-4" data-testid="tpl-envelope-panel-legacy">
                <p className="text-xs text-gray-500">Envelope settings have moved to Host Dashboard → Settings → Envelope.</p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="welcome">
            <Card>
              <CardContent className="p-6 space-y-4" data-testid="tpl-welcome-panel-legacy">
                <p className="text-xs text-gray-500">Welcome settings have moved to Host Dashboard → Settings → Welcome.</p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="intake">
            <Card>
              <CardContent className="p-6 space-y-4" data-testid="tpl-intake-panel-legacy">
                <p className="text-xs text-gray-500">Intake schema has moved to Host Dashboard → Settings → Intake.</p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="confirmation">
            <Card>
              <CardContent className="p-6 space-y-4" data-testid="tpl-confirmation-panel-legacy">
                <p className="text-xs text-gray-500">Confirmation copy has moved to Host Dashboard → Settings → Confirmation.</p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="pilot">
            <Card>
              <CardContent className="p-6 space-y-4" data-testid="tpl-pilot-panel">
                <h3 className="font-semibold text-sm">Pilot onboarding overrides</h3>
                <p className="text-xs text-gray-500">
                  Pilot onboarding rides on the canonical Envelope → Welcome → Intake → Confirmation flow with{" "}
                  <code>invite_source="pilot_onboarding"</code>. Override the copy for each step below - leave blank to use the category default.
                </p>
                <FieldTextarea
                  label="Envelope copy"
                  rows={3}
                  value={panelDrafts.pilot_onboarding?.copy_overrides?.envelope_copy || ""}
                  onChange={v => updateDraft("pilot_onboarding", "copy_overrides", { ...(panelDrafts.pilot_onboarding?.copy_overrides || {}), envelope_copy: v })}
                  testid="pilot-envelope-copy"
                />
                <FieldTextarea
                  label="Welcome copy"
                  rows={3}
                  value={panelDrafts.pilot_onboarding?.copy_overrides?.welcome_copy || ""}
                  onChange={v => updateDraft("pilot_onboarding", "copy_overrides", { ...(panelDrafts.pilot_onboarding?.copy_overrides || {}), welcome_copy: v })}
                  testid="pilot-welcome-copy"
                />
                <FieldTextarea
                  label="Intake intro"
                  rows={3}
                  value={panelDrafts.pilot_onboarding?.copy_overrides?.intake_intro || ""}
                  onChange={v => updateDraft("pilot_onboarding", "copy_overrides", { ...(panelDrafts.pilot_onboarding?.copy_overrides || {}), intake_intro: v })}
                  testid="pilot-intake-intro"
                />
                <FieldTextarea
                  label="Confirmation post-message"
                  rows={3}
                  value={panelDrafts.pilot_onboarding?.copy_overrides?.confirmation_post || ""}
                  onChange={v => updateDraft("pilot_onboarding", "copy_overrides", { ...(panelDrafts.pilot_onboarding?.copy_overrides || {}), confirmation_post: v })}
                  testid="pilot-confirmation-post"
                />
                <Button onClick={() => savePanel("pilot_onboarding")} className="bg-purple-600" data-testid="save-pilot"><Save className="w-4 h-4 mr-2" />Save Pilot Onboarding</Button>
              </CardContent>
            </Card>
          </TabsContent>
            </Tabs>
          </TabsContent>
          )}
          {/* ════════════════ END TOP TAB 1 ════════════════ */}

          {/* ════════════════ TOP TAB 2 - Host Dashboard ════════════════ */}
          <TabsContent value="host_dash">
            <Tabs defaultValue="overview">
              <TabsList className="mb-4 grid w-full grid-cols-4 max-w-3xl">
                <TabsTrigger value="overview" data-testid="tpl-tab-dashboard">Dashboard</TabsTrigger>
                <TabsTrigger value="people" data-testid="tpl-tab-clients">{clientsLabelFor(selectedCategory)}</TabsTrigger>
                <TabsTrigger value="analytics" data-testid="tpl-tab-insights">Insights</TabsTrigger>
                <TabsTrigger value="settings" data-testid="tpl-tab-settings">Settings</TabsTrigger>
              </TabsList>

          <TabsContent value="overview">
            {/* Host Kanban - matches the LIVE host dashboard view exactly (Iter 9.40 Slice C⁴).
                Lanes: Invites Sent · Preferences Shared · Complete · Archive. */}
            <EditableSection id="stats" label="Client pipeline (Kanban - what the host sees)">
              {/* Board / Calendar toggle */}
              <div className="flex items-center justify-end gap-1 mb-3" data-testid="tpl-board-calendar-toggle">
                <button className="text-xs px-2.5 py-1 rounded-md bg-violet-100 text-violet-800 font-medium" data-testid="tpl-view-board">
                  ▦ Board
                </button>
                <button className="text-xs px-2.5 py-1 rounded-md text-stone-500 hover:bg-stone-900 hover:text-white" data-testid="tpl-view-calendar">
                  ▤ Calendar
                </button>
              </div>
              <div className="flex items-center justify-end mb-2">
                <button className="text-[11px] text-stone-400 hover:text-stone-700 flex items-center gap-1" data-testid="tpl-hide-archive">
                  <EyeOff className="w-3.5 h-3.5" /> Hide Archive
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="tpl-host-kanban">
                {[
                  { id: "invites_sent",       label: "Invites Sent",       headBg: "bg-amber-50",    headText: "text-amber-800",   border: "border-amber-200",   sample: 1, sampleClient: { name: "Rachel Kim",     state: "Invite sent" } },
                  { id: "preferences_shared", label: "Preferences Shared", headBg: "bg-violet-50",   headText: "text-violet-800",  border: "border-violet-200",  sample: 1, sampleClient: { name: "Sofia Martinez", state: "Preferences shared" } },
                  { id: "complete",           label: "Complete",           headBg: "bg-emerald-50",  headText: "text-emerald-800", border: "border-emerald-200", sample: 0 },
                  { id: "archive",            label: "Archive",            headBg: "bg-stone-50",    headText: "text-stone-700",   border: "border-stone-200",   sample: 0 },
                ].map(lane => (
                  <Card key={lane.id} className={`bg-white ${lane.border} overflow-hidden`} data-testid={`tpl-kanban-${lane.id}`}>
                    {/* Header row - colored per status, rest of the card stays white */}
                    <div className={`${lane.headBg} px-4 py-2.5 flex items-center justify-between`} data-testid={`tpl-kanban-head-${lane.id}`}>
                      <p className={`text-xs font-bold uppercase tracking-wider ${lane.headText}`}>{lane.label}</p>
                      <span className={`text-xs font-semibold ${lane.headText}`}>{lane.sample}</span>
                    </div>
                    <CardContent className="p-3">
                      {lane.sample > 0 ? (
                        <SampleKanbanCard
                          laneId={lane.id}
                          client={lane.sampleClient}
                          categoryLightHex={panelDrafts.branding?.light_hex || categoryDoc?.color_light || categoryDoc?.primary_color || "#f5f5f4"}
                          enabledTagFields={(() => {
                            const fields = panelDrafts.preference_fields || categoryDoc?.preference_fields || [];
                            const intakeFlags = currentTemplate?.intake_fields || {};
                            return fields
                              .filter(f => f.enabled !== false && intakeFlags[f.field_id]?.collected !== false)
                              .map(f => ({ field_id: f.field_id, label: f.label || f.name || f.field_id }));
                          })()}
                        />
                      ) : (
                        <div className="border-2 border-dashed border-stone-200 rounded-lg p-8 text-center">
                          <p className="text-[11px] text-stone-400">No clients</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
              <p className="text-[11px] text-stone-500 mt-1">This Kanban mirrors the live host dashboard flow. Each card shows the universal preference card pop-up via the eye icon, and lane-state actions via the menu icon.</p>
            </EditableSection>
            {/* Legacy totals tiles intentionally removed per Iter 9.40 Slice C³. Reintroduce by restoring the prior stat_cards block if needed. */}
            {false && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                {/* Total Attending */}
                {currentTemplate.stat_cards?.total_attending?.enabled !== false && (
                  <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-purple-600 font-medium">
                            {currentTemplate.stat_cards?.total_attending?.label || 'Total Attending'}
                          </p>
                          <p className="text-3xl font-bold text-purple-700">--</p>
                        </div>
                        <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                          <Users className="w-6 h-6 text-purple-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* RSVPs */}
                {currentTemplate.stat_cards?.rsvps?.enabled !== false && (
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">{currentTemplate.stat_cards?.rsvps?.label || 'RSVPs'}</p>
                          <p className="text-3xl font-bold">--</p>
                        </div>
                        <Users className="w-8 h-8 text-purple-500" />
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Checked In */}
                {currentTemplate.stat_cards?.checked_in?.enabled !== false && (
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">{currentTemplate.stat_cards?.checked_in?.label || 'Checked In'}</p>
                          <p className="text-3xl font-bold">--</p>
                        </div>
                        <CheckCircle2 className="w-8 h-8 text-green-500" />
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Dietary Needs - Only if enabled */}
                {currentTemplate.stat_cards?.dietary_needs?.enabled && (
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">{currentTemplate.stat_cards?.dietary_needs?.label || 'Dietary Needs'}</p>
                          <p className="text-3xl font-bold">--</p>
                        </div>
                        <Utensils className="w-8 h-8 text-orange-500" />
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Accessibility - Only if enabled */}
                {currentTemplate.stat_cards?.accessibility?.enabled && (
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">{currentTemplate.stat_cards?.accessibility?.label || 'Accessibility'}</p>
                          <p className="text-3xl font-bold">--</p>
                        </div>
                        <Accessibility className="w-8 h-8 text-blue-500" />
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
            {/* End legacy totals dead-code (Slice C³) */}
            
            {/* Tech Stack - EDITABLE */}
            {currentTemplate.display?.show_tech_stack !== false && (
              <EditableSection id="tech-stack" label="Tech Stack">
                <Card className="mb-6">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Zap className="w-5 h-5" />
                          Tools that power your business
                        </CardTitle>
                        <CardDescription>Your business operating system</CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm">Manage →</Button>
                        <Button variant="ghost" size="sm">
                          <Info className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-4">
                      <div className="flex flex-col items-center gap-1">
                        <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                          <span className="text-gray-400 text-lg">+</span>
                        </div>
                        <span className="text-xs text-gray-500">Add Tool</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </EditableSection>
            )}
            
            {/* Insights tiles MOVED to dedicated Insights tab (Iter 9.40 Slice C⁶).
                Quick Actions section retained ONLY for the Events category. Non-Events
                hosts get a cleaner Dashboard with Kanban + Tools that power your business. */}
            {currentTemplate.display?.show_quick_actions !== false && selectedCategory === "events" && (
              <EditableSection id="quick-actions" label="Quick Actions (Events only)">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ArrowUp className="w-5 h-5" />Quick Actions
                    </CardTitle>
                    <CardDescription>Share summaries with vendors and team - events-only surface</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-3" data-testid="tpl-quick-actions-events">
                      {[
                        { id: "share_with_instructor",   l: "Share with Instructor",  s: "Send dietary/accessibility summary" },
                        { id: "share_dietary_summary",   l: "Dietary Summary",        s: "Share with catering" },
                        { id: "share_music_preferences", l: "Music Preferences",      s: "View guest music preferences" },
                        { id: "invite_to_list",          l: "Invite to List",         s: "Add people to VIP list" },
                      ].map(qa => (
                        <Button key={qa.id} variant="outline" className="justify-start h-auto py-3" data-testid={`tpl-qa-${qa.id}`}>
                          <div className="text-left">
                            <p className="font-medium">{qa.l}</p>
                            <p className="text-xs text-gray-500">{qa.s}</p>
                          </div>
                          <ExternalLink className="w-4 h-4 ml-auto" />
                        </Button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </EditableSection>
            )}
          </TabsContent>
          
          <TabsContent value="people">
            {/* Host-facing wireframe preview - table + Kanban with invite/add-new popups */}
            <ClientsWireframe
              category={selectedCategory}
              currentTemplate={currentTemplate}
              acuityEnabled={panelDrafts.integrations?.acuity !== false}
              categoryLightHex={panelDrafts.branding?.light_hex || categoryDoc?.light_hex || "#f5f5f4"}
            />
            {/* Admin config */}
            <div className="mt-4">
              <AdminOnlyNote title="Clients tab configuration (host never sees this list)" testid="clients-admin-cfg">
                Controls how the {clientsLabelFor(selectedCategory)} tab renders for hosts. Toggling a column OFF hides it across both the table and Kanban views.
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  <FieldText label="Tab label override" value={panelDrafts.clients?.tab_label} onChange={v => updateDraft("clients", "tab_label", v)} testid="clients-tab-label" />
                  <FieldText label="Empty state title" value={panelDrafts.clients?.empty_title} onChange={v => updateDraft("clients", "empty_title", v)} testid="clients-empty-title" />
                  <FieldTextarea label="Empty state description" rows={2} value={panelDrafts.clients?.empty_desc} onChange={v => updateDraft("clients", "empty_desc", v)} testid="clients-empty-desc" />
                  <FieldText label="Empty state CTA label" value={panelDrafts.clients?.empty_cta} onChange={v => updateDraft("clients", "empty_cta", v)} testid="clients-empty-cta" />
                </div>
                <div className="mt-3">
                  <p className="text-[10px] uppercase tracking-wider text-purple-700 mb-1.5">Visible columns</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { k: "show_avatar", l: "Avatar" },
                      { k: "show_email", l: "Email" },
                      { k: "show_phone", l: "Phone" },
                      { k: "show_dietary", l: "Dietary tags" },
                      { k: "show_accessibility", l: "Accessibility tags" },
                      { k: "show_last_appointment", l: "Last appointment" },
                      { k: "show_lifetime_value", l: "Lifetime value" },
                      { k: "show_kanban", l: "Kanban view" },
                    ].map(c => (
                      <FieldBool key={c.k} label={c.l} value={panelDrafts.clients?.[c.k] !== false} onChange={v => updateDraft("clients", c.k, v)} testid={`clients-${c.k}`} />
                    ))}
                  </div>
                </div>
                <Button onClick={() => savePanel("clients")} className="bg-purple-600 mt-3" data-testid="save-clients"><Save className="w-3.5 h-3.5 mr-1.5" />Save</Button>
              </AdminOnlyNote>
            </div>
          </TabsContent>

          {/* ════════════════ Intake - synced from Master Preferences + Master Inspo ════════════════ */}
          <TabsContent value="analytics">
            <Card>
              <CardContent className="p-6 space-y-5" data-testid="tpl-insights-panel">
                {/* Modern host-facing preview - what hosts see live on /me */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-sm flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-purple-600" />
                        Insights
                      </h3>
                      <p className="text-[11px] text-stone-500">Live snapshot of your client base. Updates as preferences change.</p>
                    </div>
                  </div>
                  <ModernInsightsDashboard
                    category={selectedCategory}
                    currentTemplate={currentTemplate}
                    preferenceFields={panelDrafts.preference_fields || categoryDoc?.preference_fields || []}
                    categoryLightHex={panelDrafts.branding?.light_hex || categoryDoc?.light_hex || "#f5f5f4"}
                  />
                </div>

                {/* Admin-only configuration */}
                <AdminOnlyNote title="Insights configuration (host never sees these toggles)" testid="insights-admin-cfg">
                  Toggle which buckets are eligible to surface in the host's live Insights tab. Hosts then opt into the ones they want via their own settings. Tiles bound to a preference field auto-hide when that field is OFF in intake.
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 mt-3">
                    {[
                      { k: "show_total_clients",      l: "Total clients tile" },
                      { k: "show_top_inspo",          l: "Top inspo (real-time popularity)" },
                      { k: "show_trending_products",  l: "Trending products / appointment" },
                      { k: "show_brand_clicks",       l: "Brand & product link clicks" },
                      { k: "show_preference_distribution", l: "Live preference distribution" },
                      { k: "show_revenue",            l: "Revenue tile" },
                      { k: "show_repeat_rate",        l: "Repeat-client rate" },
                      { k: "show_referral_sources",   l: "Referral sources" },
                      { k: "show_intake_trends",      l: "Intake response trends" },
                    ].map(c => (
                      <FieldBool key={c.k} label={c.l} value={panelDrafts.insights?.[c.k] !== false} onChange={v => updateDraft("insights", c.k, v)} testid={`insights-${c.k}`} />
                    ))}
                  </div>
                  <FieldTextarea label="Insights intro copy (host-visible)" rows={2} value={panelDrafts.insights?.intro_copy} onChange={v => updateDraft("insights", "intro_copy", v)} testid="insights-intro" />
                  <Button onClick={() => savePanel("insights")} className="bg-purple-600 mt-2" data-testid="save-insights"><Save className="w-3.5 h-3.5 mr-1.5" />Save Insights</Button>
                </AdminOnlyNote>

                {/* Field/source explainer - admin only */}
                <AdminOnlyNote title="Where this data comes from" testid="insights-data-sources" compact>
                  <ul className="list-disc pl-4 space-y-0.5">
                    <li><strong>Top inspo</strong> · <code className="text-[10px] bg-purple-100 px-1 rounded">db.inspo_attachments</code> grouped by image_id, sorted by attach count.</li>
                    <li><strong>Trending products</strong> · <code className="text-[10px] bg-purple-100 px-1 rounded">db.preferences.product_links</code> joined with <code className="text-[10px] bg-purple-100 px-1 rounded">bookings</code>.</li>
                    <li><strong>Brand clicks</strong> · <code className="text-[10px] bg-purple-100 px-1 rounded">events.product_click</code> grouped by brand_id.</li>
                    <li><strong>Preference distribution</strong> · live aggregation of <code className="text-[10px] bg-purple-100 px-1 rounded">db.preferences[host_id][field_id]</code> bucketed by option_id.</li>
                  </ul>
                </AdminOnlyNote>

                {/* Detailed catalog reference - admin-only collapsible */}
                <AdminOnlyNote title="Insight tile catalog (admin reference)" testid="insights-catalog-ref" compact>
                  Same 4-pillar structure (Clientele · Services · Inspo · Products). Tiles bind to <code className="text-[10px] bg-purple-100 px-1 rounded">field_id</code> and auto-hide when intake is OFF. Use this to verify which tiles ship before you toggle Visibility above.
                </AdminOnlyNote>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="settings">
            <SettingsLeftNav
              panelDrafts={panelDrafts}
              updateDraft={updateDraft}
              savePanel={savePanel}
              currentTemplate={currentTemplate}
              categoryDoc={categoryDoc}
            />
          </TabsContent>
            </Tabs>
          </TabsContent>
          {/* ════════════════ END TOP TAB 2 ════════════════ */}
        </Tabs>

        {/* Powered by Phygital footer - synced from Master Components → Logo (Iter 9.40 Slice C⁴) */}
        <div className="flex items-center justify-center gap-2 mt-8 pt-6 border-t border-stone-100" data-testid="tpl-powered-by">
          <div className="w-5 h-5 rounded bg-stone-900 flex items-center justify-center text-white text-[9px] font-bold">P</div>
          <p className="text-xs text-stone-500">Powered by Phygital</p>
        </div>
      </div>
      
      {/* Edit Dialog - Opens when clicking edit badge on a section */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Edit {activeSection === 'stats' ? 'Kanban lanes' : activeSection === 'tech-stack' ? 'Tools that power your business' : activeSection === 'quick-actions' ? 'Insights tiles' : 'Section'}
            </DialogTitle>
            <DialogDescription>
              Configure what hosts see in this section for {PILOT_CATEGORIES.find(c => c.id === selectedCategory)?.name} dashboards
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {activeSection === 'stats' && (
              <>
                <div className="space-y-4">
                  <h4 className="font-medium">Visible Kanban lanes</h4>
                  <p className="text-xs text-stone-500">These are the canonical client-pipeline lanes hosts use. Each lane reads live from the host's clients collection - labels are editable, visibility is toggleable per host.</p>
                  {Object.entries({ ...DEFAULT_TEMPLATE.stat_cards, ...currentTemplate.stat_cards })
                    .filter(([k]) => Object.keys(DEFAULT_TEMPLATE.stat_cards).includes(k))
                    .map(([key, config]) => (
                    <div key={key} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`tpl-edit-stat-${key}`}>
                      <div>
                        <Label className="capitalize">{(config.label || key.replace(/_/g, ' '))}</Label>
                        <Input
                          className="mt-1 w-64"
                          value={config.label || ''}
                          onChange={(e) => updateStatCard(key, { label: e.target.value })}
                          placeholder="Custom label..."
                        />
                        <p className="text-[10px] text-stone-400 mt-1 font-mono">key: {key}</p>
                      </div>
                      <Switch
                        checked={config.enabled !== false}
                        onCheckedChange={(checked) => updateStatCard(key, { enabled: checked })}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
            
            {activeSection === 'tech-stack' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Show Tech Stack Section</Label>
                  <Switch 
                    checked={currentTemplate.display?.show_tech_stack !== false}
                    onCheckedChange={(checked) => updateDisplay('show_tech_stack', checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Enable NFC Kiosk Check-in</Label>
                  <Switch 
                    checked={currentTemplate.display?.nfc_kiosk_enabled === true}
                    onCheckedChange={(checked) => updateDisplay('nfc_kiosk_enabled', checked)}
                  />
                </div>
              </div>
            )}
            
            {activeSection === 'quick-actions' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Show Quick Actions Section</Label>
                  <Switch 
                    checked={currentTemplate.display?.show_quick_actions !== false}
                    onCheckedChange={(checked) => updateDisplay('show_quick_actions', checked)}
                  />
                </div>
                <hr />
                <h4 className="font-medium">Available Actions</h4>
                {Object.entries(currentTemplate.quick_actions || DEFAULT_TEMPLATE.quick_actions).map(([key, enabled]) => (
                  <div key={key} className="flex items-center justify-between">
                    <Label>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</Label>
                    <Switch 
                      checked={enabled === true}
                      onCheckedChange={(checked) => updateQuickAction(key, checked)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={() => { saveTemplate(); setShowEditDialog(false); }}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Iter 9.37 - collapsible accordion for nested settings sub-sections ───
function Accordion({ title, testid, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl bg-white overflow-hidden" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 text-left"
      >
        <span className="text-sm font-medium text-gray-800">{title}</span>
        <span className="text-gray-400 text-xs">{open ? "−" : "+"}</span>
      </button>
      {open && <div className="px-4 pb-4 space-y-2 border-t border-gray-100">{children}</div>}
    </div>
  );
}

// ─── Iter 9.34: Tiny field helpers used by the new panels ─────────────────
function FieldText({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs text-gray-500">{label}</Label>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm"
        data-testid={testid}
      />
    </div>
  );
}
function FieldTextarea({ label, rows = 3, value, onChange, testid, mono }) {
  return (
    <div>
      <Label className="text-xs text-gray-500">{label}</Label>
      <textarea
        rows={rows}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1 w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm ${mono ? "font-mono" : ""}`}
        data-testid={testid}
      />
    </div>
  );
}
function FieldHex({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs text-gray-500">{label}</Label>
      <div className="flex items-center gap-2 mt-1">
        <input
          type="color"
          value={value || "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="w-9 h-9 rounded border cursor-pointer"
          data-testid={`${testid}-color`}
        />
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 border border-gray-200 rounded-md px-2 py-1.5 text-sm"
          data-testid={testid}
        />
      </div>
    </div>
  );
}
function FieldImage({ label, value, onChange, testid }) {
  const fileRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/upload/cloudinary`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` },
        body: fd,
      });
      if (r.ok) {
        const d = await r.json();
        const url = d.url || d.secure_url;
        if (url) onChange(url);
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <Label className="text-xs text-gray-500">{label}</Label>
      <div className="flex items-center gap-2 mt-1">
        <div className="relative w-12 h-12 group">
          {value ? (
            <>
              <img src={value} alt="" className="w-12 h-12 object-cover rounded border" />
              <button
                type="button"
                onClick={() => onChange("")}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-black/80 text-white text-[10px] font-bold opacity-0 group-hover:opacity-100"
                data-testid={`${testid}-remove`}
              >×</button>
            </>
          ) : (
            <div className="w-12 h-12 rounded bg-gray-100 border" />
          )}
        </div>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid={`${testid}-file`} />
        <Button size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} data-testid={`${testid}-upload`}>
          {busy ? "Uploading…" : value ? "Replace" : "Upload"}
        </Button>
      </div>
    </div>
  );
}
function FieldBool({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center gap-2 pt-5">
      <input
        type="checkbox"
        checked={!!value}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testid}
      />
      <Label className="text-xs text-gray-600">{label}</Label>
    </div>
  );
}

// ─── Settings left-nav (Iter 9.40 Slice C⁷) ─────────────────────────────────
// Replaces the legacy accordion stack with a 2-column sidebar+main layout.
function SettingsLeftNav({ panelDrafts, updateDraft, savePanel, currentTemplate, categoryDoc }) {
  // Slice I - grouped sections so Shelsea's nav feels like a workflow:
  //   Booking    = what the client sees end-to-end
  //   Operations = how the host runs their business
  //   Admin only = visibility and deeper wiring
  const GROUPS = [
    { key: "booking", label: "Booking", panes: [
      { key: "envelope",     label: "Envelope",     desc: "Logo + cover + invite copy" },
      { key: "welcome",      label: "Welcome",      desc: "First-time client greeting" },
      { key: "intake",       label: "Intake",       desc: "Preferences + base profile + inspo" },
      { key: "rsvp",         label: "RSVP",         desc: "Slot picker + attendance" },
      { key: "confirmation", label: "Confirmation", desc: "Pre / during / post + product card" },
    ]},
    { key: "operations", label: "Operations", panes: [
      { key: "services",     label: "Services",     desc: "Master + host-renamed + Acuity sync" },
      { key: "products",     label: "Products",     desc: "Recommendation pool" },
      { key: "financials",   label: "Financials",   desc: "Venmo / Zelle / Stripe" },
      { key: "media",        label: "Media library",desc: "Cover / inspo / final-look uploads" },
      { key: "about",        label: "About your business", desc: "Bio, photos, social - business side only" },
      { key: "host_portal",  label: "Host Portal",  desc: "Share · billing · brand one-pager" },
      { key: "integrations", label: "Integrations", desc: "Acuity / Stripe / Calendar / Kommo" },
    ]},
    { key: "admin", label: "Admin only", panes: [
      { key: "visibility",   label: "Visibility",   desc: "Host-facing toggle matrix" },
    ]},
  ];
  const [active, setActive] = useState("envelope");
  const acuityEnabled = panelDrafts.integrations?.acuity !== false;
  return (
    <div className="grid grid-cols-12 gap-4" data-testid="tpl-settings-leftnav">
      <aside className="col-span-12 md:col-span-3 bg-white rounded-xl border border-stone-200 p-2 h-fit md:sticky md:top-2">
        {GROUPS.map((g) => (
          <div key={g.key} className="mb-2" data-testid={`set-group-${g.key}`}>
            <p className="text-[9px] uppercase tracking-widest text-stone-400 font-semibold px-3 pt-1 pb-1">{g.label}</p>
            {g.panes.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setActive(s.key)}
                className={`w-full text-left px-3 py-2 rounded-lg mb-0.5 text-sm transition ${
                  active === s.key ? "bg-purple-50 text-purple-900 font-semibold" : "text-stone-700 hover:bg-stone-50"
                }`}
                data-testid={`set-nav-${s.key}`}
              >
                <p className={active === s.key ? "" : "font-medium"}>{s.label}</p>
                <p className="text-[10px] text-stone-400">{s.desc}</p>
              </button>
            ))}
          </div>
        ))}
      </aside>
      <main className="col-span-12 md:col-span-9 space-y-3" data-testid={`set-main-${active}`}>
        {active === "envelope" && (
          <Card><CardContent className="p-5 space-y-3" data-testid="set-pane-envelope">
            <h3 className="font-semibold text-sm">Envelope</h3>
            <FieldImage label="Logo" value={panelDrafts.envelope?.logo_url} onChange={v => updateDraft("envelope", "logo_url", v)} testid="env-logo" />
            <FieldImage label="Cover image" value={panelDrafts.envelope?.cover_image} onChange={v => updateDraft("envelope", "cover_image", v)} testid="env-cover" />
            <FieldText label="Headline" value={panelDrafts.envelope?.headline} onChange={v => updateDraft("envelope", "headline", v)} testid="env-headline" />
            <FieldTextarea label="Copy" rows={4} value={panelDrafts.envelope?.copy} onChange={v => updateDraft("envelope", "copy", v)} testid="env-copy" />
            <Button onClick={() => savePanel("envelope")} className="bg-purple-600" data-testid="save-envelope"><Save className="w-3.5 h-3.5 mr-1.5" />Save Envelope</Button>
          </CardContent></Card>
        )}
        {active === "welcome" && (
          <Card><CardContent className="p-5 space-y-3" data-testid="set-pane-welcome">
            <h3 className="font-semibold text-sm">Welcome</h3>
            <FieldImage label="Welcome image" value={panelDrafts.welcome?.image_url} onChange={v => updateDraft("welcome", "image_url", v)} testid="welcome-image" />
            <FieldTextarea label="Copy" rows={4} value={panelDrafts.welcome?.copy} onChange={v => updateDraft("welcome", "copy", v)} testid="welcome-copy" />
            <FieldBool label="Show slot picker" value={panelDrafts.welcome?.slot_picker_enabled !== false} onChange={v => updateDraft("welcome", "slot_picker_enabled", v)} testid="welcome-slot" />
            <Button onClick={() => savePanel("welcome")} className="bg-purple-600" data-testid="save-welcome"><Save className="w-3.5 h-3.5 mr-1.5" />Save Welcome</Button>
          </CardContent></Card>
        )}
        {active === "intake" && (
          <Card><CardContent className="p-5 space-y-4" data-testid="set-pane-intake">
            <h3 className="font-semibold text-sm">Intake</h3>

            <div className="border border-stone-200 rounded-lg p-3 bg-stone-50/40 space-y-2" data-testid="intake-base-profile">
              <p className="text-[11px] uppercase tracking-wider text-stone-500 font-medium">Base profile fields</p>
              <p className="text-[11px] text-stone-500">Captured + saved to the client's account on RSVP.</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { k: "ask_profile_image", l: "Profile image" },
                  { k: "ask_full_name",     l: "Full name" },
                  { k: "ask_email",         l: "Email" },
                  { k: "ask_phone",         l: "Phone" },
                  { k: "ask_pronouns",      l: "Pronouns" },
                  { k: "ask_birthday",      l: "Birthday" },
                ].map((f) => (
                  <FieldBool key={f.k} label={f.l} value={panelDrafts.intake?.[f.k] !== false} onChange={(v) => updateDraft("intake", f.k, v)} testid={`intake-base-${f.k}`} />
                ))}
              </div>
            </div>

            <div className="border border-stone-200 rounded-lg p-3 space-y-2" data-testid="intake-inspo">
              <p className="text-[11px] uppercase tracking-wider text-stone-500 font-medium">Inspo</p>
              <FieldText label="Host inspo folder name" value={panelDrafts.intake?.inspo_folder_name} onChange={(v) => updateDraft("intake", "inspo_folder_name", v)} testid="intake-inspo-folder" />
              <FieldBool label="Allow client inspo uploads" value={panelDrafts.intake?.allow_inspo_upload !== false} onChange={(v) => updateDraft("intake", "allow_inspo_upload", v)} testid="intake-inspo-upload" />
              <FieldBool label="Auto-attach client inspo to host folder" value={!!panelDrafts.intake?.auto_attach_inspo} onChange={(v) => updateDraft("intake", "auto_attach_inspo", v)} testid="intake-inspo-attach" />
            </div>

            <div>
              <p className="text-[11px] uppercase tracking-wider text-stone-500 font-medium mb-1.5">Preferences</p>
              <p className="text-xs text-stone-500 mb-2">Visual editor - same field layout the client sees during intake.</p>
              <PreferenceFieldsEditor
                value={panelDrafts.intake?.schema || []}
                onChange={(next) => updateDraft("intake", "schema", next)}
                testidPrefix="intake-schema"
              />
            </div>

            <Button onClick={() => savePanel("intake")} className="bg-purple-600" data-testid="save-intake"><Save className="w-3.5 h-3.5 mr-1.5" />Save Intake</Button>
          </CardContent></Card>
        )}
        {active === "rsvp" && (
          <Card><CardContent className="p-5 space-y-3" data-testid="set-pane-rsvp">
            <h3 className="font-semibold text-sm">RSVP</h3>
            <FieldBool label="Allow client RSVP from invite" value={panelDrafts.rsvp?.allow_rsvp !== false} onChange={v => updateDraft("rsvp", "allow_rsvp", v)} testid="rsvp-allow" />
            <FieldBool label="Require slot pick before confirming" value={!!panelDrafts.rsvp?.require_slot} onChange={v => updateDraft("rsvp", "require_slot", v)} testid="rsvp-require-slot" />
            <FieldBool label="Auto-attach client to host inspo folder" value={!!panelDrafts.rsvp?.attach_to_inspo} onChange={v => updateDraft("rsvp", "attach_to_inspo", v)} testid="rsvp-attach" />
            <FieldTextarea label="RSVP confirmation copy" rows={3} value={panelDrafts.rsvp?.copy} onChange={v => updateDraft("rsvp", "copy", v)} testid="rsvp-copy" />
            <Button onClick={() => savePanel("rsvp")} className="bg-purple-600" data-testid="save-rsvp"><Save className="w-3.5 h-3.5 mr-1.5" />Save RSVP</Button>
          </CardContent></Card>
        )}
        {active === "confirmation" && (
          <Card><CardContent className="p-5 space-y-3" data-testid="set-pane-confirmation">
            <h3 className="font-semibold text-sm">Confirmation</h3>
            <FieldImage label="Confirmation hero" value={panelDrafts.confirmation?.image_url} onChange={v => updateDraft("confirmation", "image_url", v)} testid="conf-image" />
            <FieldTextarea label="Pre" rows={3} value={panelDrafts.confirmation?.pre} onChange={v => updateDraft("confirmation", "pre", v)} testid="conf-pre" />
            <FieldTextarea label="During" rows={3} value={panelDrafts.confirmation?.during} onChange={v => updateDraft("confirmation", "during", v)} testid="conf-during" />
            <FieldTextarea label="Post" rows={3} value={panelDrafts.confirmation?.post} onChange={v => updateDraft("confirmation", "post", v)} testid="conf-post" />
            <FieldBool label="Attach linked products to confirmation" value={panelDrafts.confirmation?.attach_products !== false} onChange={v => updateDraft("confirmation", "attach_products", v)} testid="conf-attach-products" />
            <FieldBool label="Show universal preference card preview" value={panelDrafts.confirmation?.show_pref_card !== false} onChange={v => updateDraft("confirmation", "show_pref_card", v)} testid="conf-show-card" />
            <Button onClick={() => savePanel("confirmation")} className="bg-purple-600" data-testid="save-confirmation"><Save className="w-3.5 h-3.5 mr-1.5" />Save Confirmation</Button>
          </CardContent></Card>
        )}
        {active === "services" && (
          <Card><CardContent className="p-5 space-y-3" data-testid="set-pane-services">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-sm">Services</h3>
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${acuityEnabled ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-stone-100 text-stone-500 border border-stone-200"}`} data-testid="acuity-status-pill">
                Acuity sync · {acuityEnabled ? "ON" : "OFF"}
              </span>
            </div>
            <p className="text-xs text-stone-500">Search the master catalog and link services to this host. {acuityEnabled ? "Acuity-connected hosts can link an Acuity service to a master entry, or clone the Acuity entry." : "Acuity is OFF - turn it on under Integrations to enable service syncing."}</p>
            <ServicesPicker
              value={panelDrafts.linked_services || []}
              onChange={(next) => updateDraft("linked_services", "list", next)}
              acuityEnabled={acuityEnabled}
              category={categoryDoc?.category_id}
            />
            <Button onClick={() => savePanel("linked_services")} className="bg-purple-600" data-testid="save-linked-services"><Save className="w-3.5 h-3.5 mr-1.5" />Save services</Button>
          </CardContent></Card>
        )}
        {active === "products" && (
          <Card><CardContent className="p-5 space-y-3" data-testid="set-pane-products">
            <h3 className="font-semibold text-sm">Products</h3>
            <p className="text-xs text-stone-500">Master list at <a href="/admin/product/master-products" className="text-purple-600 underline">/admin/product/master-products</a>.</p>
            <Input
              type="text"
              placeholder="Add product ID"
              value={panelDrafts.linked_products_input || ""}
              onChange={(e) => updateDraft("linked_products_input", "value", e.target.value)}
              data-testid="linked-products-input"
            />
            <Button onClick={() => savePanel("linked_products")} className="bg-purple-600" data-testid="save-linked-products"><Save className="w-3.5 h-3.5 mr-1.5" />Save products</Button>
          </CardContent></Card>
        )}
        {active === "media" && (
          <Card><CardContent className="p-5 space-y-3" data-testid="set-pane-media">
            <h3 className="font-semibold text-sm">Media library</h3>
            <p className="text-xs text-stone-500">Reusable images for cover / inspo placeholders / final looks. Uploaded once, referenced everywhere.</p>
            <FieldImage label="Default cover" value={panelDrafts.media?.default_cover} onChange={v => updateDraft("media", "default_cover", v)} testid="media-default-cover" />
            <FieldImage label="Default inspo placeholder" value={panelDrafts.media?.default_inspo} onChange={v => updateDraft("media", "default_inspo", v)} testid="media-default-inspo" />
            <FieldImage label="Default final-look placeholder" value={panelDrafts.media?.default_final_look} onChange={v => updateDraft("media", "default_final_look", v)} testid="media-default-final-look" />
            <Button onClick={() => savePanel("media")} className="bg-purple-600" data-testid="save-media"><Save className="w-3.5 h-3.5 mr-1.5" />Save media</Button>
          </CardContent></Card>
        )}
        {active === "financials" && (
          <Card><CardContent className="p-5 space-y-3" data-testid="set-pane-financials">
            <h3 className="font-semibold text-sm">Financials</h3>
            <p className="text-xs text-stone-500">Payment handles displayed on confirmation pages and host invoices.</p>
            <FieldText label="Venmo handle" value={panelDrafts.financials?.venmo} onChange={v => updateDraft("financials", "venmo", v)} placeholder="@yourhandle" testid="fin-venmo" />
            <FieldText label="Zelle email or phone" value={panelDrafts.financials?.zelle} onChange={v => updateDraft("financials", "zelle", v)} testid="fin-zelle" />
            <FieldText label="Cash App $cashtag" value={panelDrafts.financials?.cashapp} onChange={v => updateDraft("financials", "cashapp", v)} testid="fin-cashapp" />
            <FieldBool label="Show Venmo on confirmation" value={panelDrafts.financials?.show_venmo !== false} onChange={v => updateDraft("financials", "show_venmo", v)} testid="fin-show-venmo" />
            <FieldBool label="Show Zelle on confirmation" value={panelDrafts.financials?.show_zelle !== false} onChange={v => updateDraft("financials", "show_zelle", v)} testid="fin-show-zelle" />
            <FieldBool label="Show Cash App on confirmation" value={!!panelDrafts.financials?.show_cashapp} onChange={v => updateDraft("financials", "show_cashapp", v)} testid="fin-show-cashapp" />
            <FieldBool label="Stripe checkout (subscription only)" value={!!panelDrafts.financials?.stripe_subscription} onChange={v => updateDraft("financials", "stripe_subscription", v)} testid="fin-stripe-sub" />
            <Button onClick={() => savePanel("financials")} className="bg-purple-600" data-testid="save-financials"><Save className="w-3.5 h-3.5 mr-1.5" />Save financials</Button>
          </CardContent></Card>
        )}
        {active === "about" && (
          <Card><CardContent className="p-5 space-y-3" data-testid="set-pane-about">
            <div>
              <h3 className="font-semibold text-sm">About your business</h3>
              <p className="text-[11px] text-stone-500 mt-0.5">This is business info shown on your host page. Your personal /me profile lives separately - clients never see it mixed in here.</p>
            </div>
            <FieldText label="Business name" value={panelDrafts.about?.business_name} onChange={v => updateDraft("about", "business_name", v)} placeholder="e.g. Shelsea Makeup Studio" testid="about-business-name" />
            <FieldImage label="Business logo" value={panelDrafts.about?.business_logo_url} onChange={v => updateDraft("about", "business_logo_url", v)} testid="about-logo" />
            <FieldImage label="Studio / cover photo" value={panelDrafts.about?.headshot_url} onChange={v => updateDraft("about", "headshot_url", v)} testid="about-cover" />
            <FieldTextarea label="Business bio" rows={4} value={panelDrafts.about?.bio} onChange={v => updateDraft("about", "bio", v)} placeholder="What your business does, who you serve, what makes it special." testid="about-bio" />
            <FieldText label="Business email" value={panelDrafts.about?.business_email} onChange={v => updateDraft("about", "business_email", v)} placeholder="hello@yourstudio.com" testid="about-business-email" />
            <FieldText label="Business phone" value={panelDrafts.about?.business_phone} onChange={v => updateDraft("about", "business_phone", v)} testid="about-business-phone" />
            <FieldText label="Studio address / city" value={panelDrafts.about?.business_address} onChange={v => updateDraft("about", "business_address", v)} testid="about-business-address" />
            <div className="border-t border-stone-100 pt-2 mt-2">
              <p className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Business socials</p>
            </div>
            <FieldText label="Instagram" value={panelDrafts.about?.instagram} onChange={v => updateDraft("about", "instagram", v)} placeholder="@yourstudio" testid="about-instagram" />
            <FieldText label="TikTok" value={panelDrafts.about?.tiktok} onChange={v => updateDraft("about", "tiktok", v)} placeholder="@yourstudio" testid="about-tiktok" />
            <FieldText label="Pinterest" value={panelDrafts.about?.pinterest} onChange={v => updateDraft("about", "pinterest", v)} testid="about-pinterest" />
            <FieldText label="Website" value={panelDrafts.about?.website} onChange={v => updateDraft("about", "website", v)} testid="about-website" />
            <Button onClick={() => savePanel("about")} className="bg-purple-600" data-testid="save-about"><Save className="w-3.5 h-3.5 mr-1.5" />Save business info</Button>
          </CardContent></Card>
        )}
        {active === "host_portal" && (
          <Card><CardContent className="p-5 space-y-3" data-testid="set-pane-host-portal">
            <h3 className="font-semibold text-sm">Host Portal</h3>
            <p className="text-xs text-stone-500">Quick links the host pins to their dashboard.</p>
            <FieldBool label="Show 'Share my dashboard' button" value={panelDrafts.host_portal?.show_share !== false} onChange={v => updateDraft("host_portal", "show_share", v)} testid="hp-share" />
            <FieldBool label="Show billing & subscription tile" value={panelDrafts.host_portal?.show_billing !== false} onChange={v => updateDraft("host_portal", "show_billing", v)} testid="hp-billing" />
            <FieldBool label="Show brand one-pager link" value={panelDrafts.host_portal?.show_brand_onepager !== false} onChange={v => updateDraft("host_portal", "show_brand_onepager", v)} testid="hp-brand-onepager" />
            <FieldBool label="Show invite-host CTA" value={!!panelDrafts.host_portal?.show_invite_host} onChange={v => updateDraft("host_portal", "show_invite_host", v)} testid="hp-invite-host" />
            <FieldBool label="Show export client data" value={!!panelDrafts.host_portal?.show_export} onChange={v => updateDraft("host_portal", "show_export", v)} testid="hp-export" />
            <Button onClick={() => savePanel("host_portal")} className="bg-purple-600" data-testid="save-host-portal"><Save className="w-3.5 h-3.5 mr-1.5" />Save host portal</Button>
          </CardContent></Card>
        )}
        {active === "integrations" && (
          <Card><CardContent className="p-5 space-y-3" data-testid="set-pane-integrations">
            <h3 className="font-semibold text-sm">Integrations</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {[
                { k: "acuity", l: "Acuity Scheduling" },
                { k: "stripe", l: "Stripe payouts" },
                { k: "google_calendar", l: "Google Calendar" },
                { k: "tap_in", l: "Tap In" },
                { k: "kommo", l: "Kommo CRM (DM ingest)" },
                { k: "instagram", l: "Instagram DMs" },
                { k: "cloudinary", l: "Media Library uploads" },
              ].map(c => (
                <FieldBool key={c.k} label={c.l} value={panelDrafts.integrations?.[c.k] !== false} onChange={v => updateDraft("integrations", c.k, v)} testid={`integ-${c.k}`} />
              ))}
            </div>
            <Button onClick={() => savePanel("integrations")} className="bg-purple-600" data-testid="save-integrations"><Save className="w-3.5 h-3.5 mr-1.5" />Save integrations</Button>
          </CardContent></Card>
        )}
        {active === "visibility" && (
          <AdminOnlyNote title="Visibility toggles (host never sees this list)" testid="settings-admin-cfg">
            Decide which Settings sub-sections are exposed inside the host's actual /me Settings page. Toggling OFF here hides the entire surface from the host UI.
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 mt-3">
              {[
                { k: "show_branding", l: "Branding (cover/logo)" },
                { k: "show_team_members", l: "Team members" },
                { k: "show_integrations", l: "Integrations" },
                { k: "show_acuity", l: "Acuity sync" },
                { k: "show_stripe", l: "Stripe payouts" },
                { k: "show_instructor_addon", l: "Instructor add-on CTA" },
                { k: "show_event_addon", l: "Experience add-on CTA" },
                { k: "show_billing", l: "Billing & subscription" },
                { k: "show_export", l: "Export client data" },
                { k: "show_danger_zone", l: "Danger zone (deactivate)" },
                { k: "show_services", l: "Services catalog" },
                { k: "show_products", l: "Products catalog" },
                { k: "show_rsvp", l: "RSVP surface" },
                { k: "show_media_library", l: "Media library" },
                { k: "show_financials", l: "Financials" },
                { k: "show_about", l: "About / bio" },
                { k: "show_host_portal", l: "Host Portal" },
              ].map(c => (
                <FieldBool key={c.k} label={c.l} value={panelDrafts.host_settings?.[c.k] !== false} onChange={v => updateDraft("host_settings", c.k, v)} testid={`host-settings-${c.k}`} />
              ))}
            </div>
            <Button onClick={() => savePanel("host_settings")} className="bg-purple-600 mt-3" data-testid="save-host-settings"><Save className="w-3.5 h-3.5 mr-1.5" />Save visibility</Button>
          </AdminOnlyNote>
        )}
      </main>
    </div>
  );
}

// ClientsWireframe - host-facing preview of the Clients tab (Iter 9.40 Slice C⁷).
// Shows the canonical table + Kanban view AND the two action popups: invite a
// client (sends a magic link with a preference-card invite) and add new (with
// optional Acuity link → copies Acuity client info into a new Phygital account).
function ClientsWireframe({ category, currentTemplate, acuityEnabled, categoryLightHex }) {
  const [view, setView] = useState("table");
  const [showInvite, setShowInvite] = useState(false);
  const [showAddNew, setShowAddNew] = useState(false);
  const [acuityChoice, setAcuityChoice] = useState("manual"); // 'manual' | 'acuity'
  return (
    <Card>
      <CardContent className="p-5 space-y-4" data-testid="tpl-clients-wireframe">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-sm">Clients · template preview</h3>
            <p className="text-[11px] text-stone-500">What hosts see on /me · {clientsLabelFor(category)} tab.</p>
          </div>
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant={view === "table" ? "default" : "outline"} onClick={() => setView("table")} data-testid="clients-view-table">Table</Button>
            <Button size="sm" variant={view === "kanban" ? "default" : "outline"} onClick={() => setView("kanban")} data-testid="clients-view-kanban">Kanban</Button>
            <Button size="sm" onClick={() => setShowInvite(true)} className="bg-purple-600" data-testid="clients-invite-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Invite
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowAddNew(true)} data-testid="clients-add-new-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add new
            </Button>
          </div>
        </div>
        {view === "table" ? (
          <div className="border border-stone-200 rounded-lg overflow-hidden" data-testid="clients-table-preview">
            <table className="w-full text-xs">
              <thead className="bg-stone-50 border-b border-stone-100">
                <tr>
                  {["Name", "Service", "State", "Last appt", "Tags", ""].map((h, i) => (
                    <th key={i} className="text-left px-3 py-2 font-medium text-stone-600">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { name: "Sofia Martinez",  service: "Bridal Glam",      state: "Confirmed",   last: "Apr 11", tags: ["Bridal", "Sensitive skin"] },
                  { name: "Rachel Kim",      service: "Soft Glam",        state: "Invited",     last: "Apr 02", tags: ["Latex-free"] },
                  { name: "Aliyah Johnson",  service: "Editorial Glam",   state: "Returning",   last: "Mar 24", tags: ["Oily skin", "Brand: MAC"] },
                ].map((row, i) => (
                  <tr key={i} className="border-t border-stone-100" data-testid={`clients-row-${i}`}>
                    <td className="px-3 py-2 flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-stone-900 text-white flex items-center justify-center text-[10px] font-bold">{row.name.charAt(0)}</div>
                      <span>{row.name}</span>
                    </td>
                    <td className="px-3 py-2 text-stone-600">{row.service}</td>
                    <td className="px-3 py-2 text-stone-500">{row.state}</td>
                    <td className="px-3 py-2 text-stone-500">{row.last}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {row.tags.map((t, ti) => (
                          <span key={ti} className="text-[10px] px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-700">{t}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Eye className="w-3.5 h-3.5 inline-block text-stone-400" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="border border-stone-200 rounded-lg p-3 bg-stone-50">
            <p className="text-[11px] text-stone-500 italic text-center">Kanban view - see Dashboard tab for the full canonical lane preview.</p>
          </div>
        )}

        {/* Invite popup wireframe */}
        <Dialog open={showInvite} onOpenChange={setShowInvite}>
          <DialogContent className="max-w-sm" data-testid="clients-invite-popup">
            <DialogHeader>
              <DialogTitle>Invite a client</DialogTitle>
              <DialogDescription>Sends an invite + magic link so they can fill the preference card.</DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <FieldText label="Client name" value="" onChange={() => {}} testid="invite-name" />
              <FieldText label="Email or phone" value="" onChange={() => {}} testid="invite-contact" />
              <FieldTextarea label="Personal note (optional)" rows={3} value="" onChange={() => {}} testid="invite-note" />
              <p className="text-[10px] text-stone-500">An invite email + SMS will go out with the canonical preference card. Once submitted, the client lands in the <strong>Preferences Shared</strong> Kanban lane.</p>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" onClick={() => setShowInvite(false)}>Cancel</Button>
              <Button className="bg-purple-600" data-testid="invite-send">Send invite</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add new popup wireframe */}
        <Dialog open={showAddNew} onOpenChange={setShowAddNew}>
          <DialogContent className="max-w-md" data-testid="clients-add-new-popup">
            <DialogHeader>
              <DialogTitle>Add a new client</DialogTitle>
              <DialogDescription>
                Create a Phygital account on this client's behalf. {acuityEnabled && "If Acuity is connected, link an existing Acuity client to auto-copy contact info."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="flex items-center gap-2 border-b border-stone-100 pb-2">
                <Button size="sm" variant={acuityChoice === "manual" ? "default" : "outline"} onClick={() => setAcuityChoice("manual")} data-testid="add-new-mode-manual">Manual</Button>
                <Button
                  size="sm"
                  variant={acuityChoice === "acuity" ? "default" : "outline"}
                  onClick={() => setAcuityChoice("acuity")}
                  disabled={!acuityEnabled}
                  data-testid="add-new-mode-acuity"
                >Link from Acuity</Button>
                {!acuityEnabled && <span className="text-[10px] text-stone-400">(Acuity not connected)</span>}
              </div>
              {acuityChoice === "manual" ? (
                <>
                  <FieldText label="First name" value="" onChange={() => {}} testid="add-fname" />
                  <FieldText label="Last name" value="" onChange={() => {}} testid="add-lname" />
                  <FieldText label="Email" value="" onChange={() => {}} testid="add-email" />
                  <FieldText label="Phone" value="" onChange={() => {}} testid="add-phone" />
                </>
              ) : (
                <>
                  <Input type="text" placeholder="Search Acuity clients…" data-testid="acuity-client-search" />
                  <p className="text-[10px] text-stone-500">Selecting an Acuity client copies their contact info into a brand-new Phygital account and links the two records together. Future Acuity bookings will auto-attach.</p>
                </>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" onClick={() => setShowAddNew(false)}>Cancel</Button>
              <Button className="bg-purple-600" data-testid="add-new-save">Create</Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

function ServicesPicker({ value = [], onChange, acuityEnabled, category }) {
  const [query, setQuery] = useState("");
  const [masterList, setMasterList] = useState([]);
  const [acuityList, setAcuityList] = useState([]);
  useEffect(() => {
    const token = localStorage.getItem("session_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/master-services?category=${encodeURIComponent(category || "")}`, { headers })
      .then(r => r.ok ? r.json() : { services: [] })
      .then(d => setMasterList(d.services || d || []))
      .catch(() => {});
    if (acuityEnabled) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/acuity/services`, { headers })
        .then(r => r.ok ? r.json() : { services: [] })
        .then(d => setAcuityList(d.services || d || []))
        .catch(() => {});
    }
  }, [category, acuityEnabled]);

  const matches = masterList.filter(s => (s.name || "").toLowerCase().includes(query.toLowerCase())).slice(0, 6);
  const isLinked = (id) => value.some(v => v.master_id === id || v === id);

  return (
    <div className="space-y-3">
      <Input
        type="text"
        placeholder="Search master services…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        data-testid="services-picker-search"
      />
      {query.trim() && matches.length > 0 && (
        <div className="border border-stone-200 rounded-md divide-y divide-stone-100 bg-white">
          {matches.map(m => (
            <button
              key={m.service_id || m.id}
              type="button"
              onClick={() => onChange([...value, { master_id: m.service_id || m.id, label: m.name, source: "master" }])}
              className="w-full px-3 py-2 text-left text-xs hover:bg-stone-50 flex items-center justify-between disabled:opacity-50"
              disabled={isLinked(m.service_id || m.id)}
              data-testid={`services-master-${m.service_id || m.id}`}
            >
              <span>{m.name}</span>
              <Plus className="w-3.5 h-3.5 text-stone-400" />
            </button>
          ))}
        </div>
      )}
      {acuityEnabled && acuityList.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wider text-stone-500 mb-1.5">Acuity services</p>
          <div className="space-y-1">
            {acuityList.slice(0, 6).map(a => (
              <div key={a.id} className="flex items-center justify-between bg-stone-50 rounded px-3 py-1.5 text-xs">
                <span className="truncate">{a.name}</span>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => onChange([...value, { acuity_id: a.id, master_id: null, label: a.name, source: "acuity_clone" }])} data-testid={`acuity-clone-${a.id}`}>Clone</Button>
                  <Button size="sm" variant="outline" onClick={() => onChange([...value, { acuity_id: a.id, master_id: null, label: a.name, source: "acuity_link" }])} data-testid={`acuity-link-${a.id}`}>Link</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {value.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wider text-stone-500 mb-1.5">Linked ({value.length})</p>
          <div className="space-y-1">
            {value.map((v, i) => (
              <div key={i} className="flex items-center justify-between bg-purple-50 border border-purple-100 rounded px-3 py-1.5 text-xs" data-testid={`linked-service-${i}`}>
                <span className="truncate">{v.label || v}</span>
                <span className="text-[10px] text-purple-600 font-mono ml-2">{v.source || "manual"}</span>
                <button type="button" onClick={() => onChange(value.filter((_, idx) => idx !== i))} className="text-stone-400 hover:text-red-500 ml-2">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Insights catalog ────────────────────────────────────────────────────────
// Modern host-facing dashboard - what hosts see on their live /me Insights tab.
// All numbers are placeholders ("-") until backend aggregation queries land;
// the structure, charts, and tile types are the canonical layout.

function ModernInsightsDashboard({ category, currentTemplate, preferenceFields = [], categoryLightHex = "#f5f5f4" }) {
  const insightsCfg = currentTemplate?.insights || {};
  const isEnabled = (k) => insightsCfg[k] !== false;
  const intakeFlags = currentTemplate?.intake_fields || {};

  // Iter 9.40 Slice C⁸ - fetch live aggregations.
  const [atlas, setAtlas] = useState(null);
  useEffect(() => {
    if (!category) return;
    const token = localStorage.getItem("session_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/insights/${category}/atlas`, { headers })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setAtlas(d))
      .catch(() => setAtlas(null));
  }, [category]);

  // Slice H - drill-down state for the live distribution panel.
  const [drillTarget, setDrillTarget] = useState(null); // { field, option }
  const [drillClients, setDrillClients] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const openDrillDown = useCallback(async ({ field, option }) => {
    setDrillTarget({ field, option });
    setDrillClients(null);
    setDrillLoading(true);
    const token = localStorage.getItem("session_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const fid = field.field_id || field.key;
    const oid = option.id || option.label;
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/insights/${category}/atlas/clients?field_id=${encodeURIComponent(fid)}&option_id=${encodeURIComponent(oid)}`, { headers });
      const d = r.ok ? await r.json() : { clients: [] };
      setDrillClients(d.clients || []);
    } catch {
      setDrillClients([]);
    }
    setDrillLoading(false);
  }, [category]);
  const closeDrillDown = () => { setDrillTarget(null); setDrillClients(null); };

  const fmt = (n) => (n === undefined || n === null ? "-" : (typeof n === "number" ? n.toLocaleString() : n));

  // Build distribution slices from live atlas if available; fallback to schema.
  const distributionFromAtlas = atlas?.distribution || [];
  const distributionFields = distributionFromAtlas.length
    ? distributionFromAtlas
    : preferenceFields
        .filter(f => f.enabled !== false && (intakeFlags[f.key || f.field_id]?.collected !== false))
        .filter(f => Array.isArray(f.options) && f.options.length > 0)
        .slice(0, 4)
        .map(f => ({ field_id: f.key || f.field_id, label: f.label || f.key, buckets: (f.options || []).slice(0, 6), total: 0 }));

  const TOP_TILES = [
    { k: "show_total_clients",          icon: Users,        label: "Total clients",      value: fmt(atlas?.kpis?.total_clients) },
    { k: "show_revenue",                icon: TrendingUp,   label: "Revenue (90 days)",  value: atlas?.kpis?.revenue_90d != null ? `$${fmt(atlas.kpis.revenue_90d)}` : "-" },
    { k: "show_repeat_rate",            icon: Heart,        label: "Repeat-client rate", value: atlas?.kpis?.repeat_rate != null ? `${atlas.kpis.repeat_rate}%` : "-" },
    { k: "show_brand_clicks",           icon: ExternalLink, label: "Brand clicks",       value: fmt(atlas?.kpis?.brand_clicks) },
  ].filter(t => isEnabled(t.k));

  const topInspo  = atlas?.top_inspo || [];
  const trending  = atlas?.trending_products || [];
  const topBrands = atlas?.top_brands || [];

  return (
    <div className="space-y-4" data-testid="tpl-insights-modern">
      {/* Top KPI tiles */}
      {TOP_TILES.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5" data-testid="tpl-insights-kpis">
          {TOP_TILES.map((t) => {
            const Icon = t.icon;
            return (
              <div key={t.k} className="rounded-xl border border-stone-200 bg-white p-3" data-testid={`tpl-kpi-${t.k}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] uppercase tracking-wider text-stone-500">{t.label}</span>
                  <Icon className="w-3.5 h-3.5 text-stone-400" />
                </div>
                <p className="text-2xl font-bold text-stone-900">{t.value}</p>
                <p className="text-[10px] text-stone-400 font-medium">live · last 90d</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Top inspo */}
      {isEnabled("show_top_inspo") && (
        <div className="rounded-xl border border-stone-200 bg-white p-4" data-testid="tpl-insights-top-inspo">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-stone-700 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-purple-500" /> Trending inspo
            </h4>
            <span className="text-[10px] text-stone-400">Real-time popularity · last 30 days</span>
          </div>
          {topInspo.length === 0 ? (
            <div className="rounded-lg border border-dashed border-stone-200 bg-stone-50 p-6 text-center" data-testid="tpl-insights-top-inspo-empty">
              <Sparkles className="w-5 h-5 text-stone-300 mx-auto mb-1" />
              <p className="text-xs text-stone-500">No trending inspo yet - comes alive once clients start attaching mood-board images to bookings.</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {[0, 1, 2].map((i) => {
                const t = topInspo[i];
                return (
                  <div key={i} className="space-y-1.5">
                    <div className="aspect-[4/5] rounded-lg overflow-hidden border border-stone-100" style={{ backgroundColor: categoryLightHex }}>
                      {t?.image_url && <img src={t.image_url} alt={`inspo-${i}`} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />}
                    </div>
                    <div className="flex items-center justify-between text-[10px]">
                      <span className="text-stone-600 truncate">{t ? `Inspo #${i + 1}` : "-"}</span>
                      <span className="text-stone-400 font-mono">{t ? `${t.count}` : "-"}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Trending products + brand clicks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {isEnabled("show_trending_products") && (
          <div className="rounded-xl border border-stone-200 bg-white p-4" data-testid="tpl-insights-trending-products">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-bold uppercase tracking-wider text-stone-700 flex items-center gap-1.5">
                <Package className="w-3.5 h-3.5 text-emerald-500" /> Trending products
              </h4>
              <span className="text-[10px] text-stone-400">Per appointment · last 30 days</span>
            </div>
            <div className="space-y-2">
              {[0, 1, 2, 3].map((i) => {
                const p = trending[i];
                const max = trending[0]?.count || 1;
                const pct = p ? Math.max(8, Math.round(100 * p.count / max)) : 0;
                return (
                  <div key={i} className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded bg-stone-100 border border-stone-200 shrink-0 overflow-hidden">
                      {p?.image_url && <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-stone-800 truncate">{p?.name || "-"}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <div className="flex-1 h-1.5 rounded bg-stone-100 overflow-hidden">
                          <div className="h-full bg-emerald-400" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-[10px] text-stone-500 font-mono">{p ? `${p.count}` : "-"}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {isEnabled("show_brand_clicks") && (
          <div className="rounded-xl border border-stone-200 bg-white p-4" data-testid="tpl-insights-brand-clicks">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-bold uppercase tracking-wider text-stone-700 flex items-center gap-1.5">
                <ExternalLink className="w-3.5 h-3.5 text-blue-500" /> Brand link clicks
              </h4>
              <span className="text-[10px] text-stone-400">Owned + linked brands</span>
            </div>
            <div className="space-y-2">
              {[0, 1, 2, 3].map((i) => {
                const b = topBrands[i];
                return (
                  <div key={i} className="flex items-center justify-between gap-2 text-xs">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-5 h-5 rounded bg-stone-100 border border-stone-200 overflow-hidden">
                        {b?.logo_url && <img src={b.logo_url} alt={b.name} className="w-full h-full object-contain" onError={(e) => { e.target.style.display = "none"; }} />}
                      </div>
                      <span className="text-stone-700 truncate">{b?.name || "-"}</span>
                    </div>
                    <span className="text-stone-400 font-mono text-[10px]">{b ? `${b.count} clicks` : "-"}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Live preference distribution */}
      {isEnabled("show_preference_distribution") && distributionFields.length > 0 && (
        <div className="rounded-xl border border-stone-200 bg-white p-4" data-testid="tpl-insights-distribution">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-stone-700 flex items-center gap-1.5">
              <BarChart3 className="w-3.5 h-3.5 text-purple-500" /> Live preference distribution
            </h4>
            <span className="text-[10px] text-stone-400">Click any row to see clients</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {distributionFields.map((f) => {
              const buckets = f.buckets || [];
              const max = (buckets[0]?.count || buckets[0]?.pct) || 1;
              return (
                <div key={f.field_id || f.key} data-testid={`tpl-dist-${f.field_id || f.key}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-[11px] font-semibold text-stone-800">{f.label}</p>
                    {typeof f.total === "number" && <span className="text-[10px] text-stone-400">{f.total} clients</span>}
                  </div>
                  <div className="space-y-1.5">
                    {buckets.map((o, idx) => {
                      const pct = o.pct != null ? o.pct : (o.count != null ? Math.round(100 * o.count / max) : 100 - idx * 18);
                      const fid = f.field_id || f.key;
                      const oid = o.id || o.label;
                      const drillOk = (o.count || 0) > 0;
                      return (
                        <button
                          key={o.id || idx}
                          type="button"
                          onClick={() => drillOk && openDrillDown({ field: f, option: o })}
                          className={`w-full flex items-center gap-2 group ${drillOk ? "cursor-pointer hover:bg-purple-50/40 rounded px-1 -mx-1 py-0.5" : "cursor-default"}`}
                          data-testid={`tpl-dist-bucket-${fid}-${oid}`}
                        >
                          <div
                            className="w-4 h-4 rounded shrink-0 overflow-hidden border border-stone-100"
                            style={{ backgroundColor: o.swatch || categoryLightHex }}
                          >
                            {o.image_url && <img src={o.image_url} alt={o.label} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />}
                          </div>
                          <span className="text-[10px] text-stone-700 w-20 truncate text-left">{o.label}</span>
                          <div className="flex-1 h-2 rounded bg-stone-100 overflow-hidden">
                            <div className="h-full bg-purple-400 group-hover:bg-purple-500 transition-colors" style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
                          </div>
                          <span className="text-[10px] text-stone-400 font-mono w-10 text-right">{o.count != null ? o.count : "-"}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* If host has nothing enabled */}
      {TOP_TILES.length === 0 && distributionFields.length === 0 && !isEnabled("show_top_inspo") && (
        <div className="rounded-xl border border-dashed border-stone-200 bg-stone-50 p-8 text-center" data-testid="tpl-insights-empty">
          <p className="text-xs text-stone-500">Enable at least one Insights bucket below to see live data here.</p>
        </div>
      )}

      {/* Slice H - drill-down panel */}
      {drillTarget && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40" onClick={closeDrillDown} data-testid="insights-drilldown-backdrop" />
          <aside className="fixed top-0 right-0 h-full w-[420px] bg-white shadow-xl z-50 overflow-y-auto" data-testid="insights-drilldown-drawer">
            <div className="sticky top-0 bg-white border-b border-stone-100 px-4 py-3 flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-stone-400">{drillTarget.field.label}</p>
                <h3 className="text-sm font-semibold truncate flex items-center gap-2">
                  <span className="w-3 h-3 rounded inline-block" style={{ backgroundColor: drillTarget.option.swatch || categoryLightHex }} />
                  {drillTarget.option.label}
                  {typeof drillTarget.option.count === "number" && <span className="text-xs text-stone-400 font-normal">· {drillTarget.option.count} clients</span>}
                </h3>
              </div>
              <button onClick={closeDrillDown} className="text-stone-400 hover:text-stone-700" data-testid="insights-drilldown-close"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4">
              {drillLoading && (
                <div className="flex items-center gap-2 text-xs text-stone-400 py-6 justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading clients…
                </div>
              )}
              {!drillLoading && drillClients && drillClients.length === 0 && (
                <div className="py-8 text-center text-xs text-stone-400 border border-dashed rounded-lg">
                  No clients picked this option yet.
                </div>
              )}
              {!drillLoading && drillClients && drillClients.length > 0 && (
                <ul className="space-y-1.5" data-testid="insights-drilldown-clients">
                  {drillClients.map((c) => (
                    <li key={c.client_id} className="flex items-center gap-2 p-2 rounded border border-stone-100 hover:bg-stone-50">
                      <div className="w-7 h-7 rounded-full bg-stone-200 overflow-hidden flex items-center justify-center text-[10px] text-stone-600 font-semibold shrink-0">
                        {c.profile_image_url ? <img src={c.profile_image_url} alt="" className="w-full h-full object-cover" /> : (c.name || c.email || "?").charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-stone-900 truncate">{c.name || c.email}</p>
                        {c.email && <p className="text-[10px] text-stone-400 truncate">{c.email}</p>}
                      </div>
                      {c.updated_at && <span className="text-[9px] text-stone-300 font-mono">{(c.updated_at || "").slice(0, 10)}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </aside>
        </>
      )}
    </div>
  );
}

const MAKEUP_INSIGHTS = {
  clientele: [
    { tile: "# clients per allergen",                  field_id: "allergens" },
    { tile: "# clients per sensitivity",               field_id: "sensitivities" },
    { tile: "# clients per skin type × undertone",     field_id: "skin_type_undertone" },
    { tile: "# clients per foundation shade",          field_id: "foundation_shade" },
    { tile: "# clients per skin tone metal",           field_id: "skin_tone_metal" },
    { tile: "# clients per eye shape",                 field_id: "eye_shape" },
    { tile: "# clients per lash style",                field_id: "lash_style" },
    { tile: "# clients per lash strip brand",          field_id: "lash_strip_brand" },
    { tile: "# clients per lip shape",                 field_id: "lip_shape" },
    { tile: "# clients per lip finish",                field_id: "lip_finish" },
    { tile: "# clients per skin concern",              field_id: "skin_concerns" },
    { tile: "# clients per brand affinity",            field_id: "brand_affinity" },
  ],
  services: [
    { tile: "Top service group",                       source: "db.master_services + bookings" },
    { tile: "Top master service",                      source: "bookings" },
    { tile: "Bridal mix",                              source: "bookings.bridal_flag" },
    { tile: "Average revenue",                         source: "stripe + bookings.amount" },
    { tile: "Acuity vs manual",                        source: "bookings.source" },
    { tile: "Linking %",                               source: "preferences.linked_to_host" },
    { tile: "Preference sharing completion rate",      source: "invites + preferences.shared_at" },
    { tile: "Repeat-client rate",                      source: "bookings GROUP BY client" },
    { tile: "Final look upload rate",                  source: "bookings.final_look_url" },
  ],
  inspo: [
    { tile: "Top host inspo",                          source: "db.host_inspo + clicks" },
    { tile: "Most-attached private inspo",             source: "db.host_client_inspo" },
    { tile: "Inspo-to-service conversion",             source: "inspo_clicks → bookings" },
  ],
  products: [
    { tile: "Most-linked products",                    source: "db.preferences.product_links" },
    { tile: "Top per service",                         source: "preferences GROUP BY service_group" },
    { tile: "Top brands",                              source: "db.master_brands + product_links" },
    { tile: "Product link clicks",                     source: "events.product_click" },
    { tile: "Upsell add-ons",                          source: "bookings.upsells" },
    { tile: "Product co-occurrence",                   source: "preferences.product_links matrix" },
    { tile: "Pending UGC",                             source: "db.master_products WHERE status='pending'" },
  ],
};

// Same 4-pillar catalog structure mirrored for other categories. Each clientele
// tile binds to a preference field id and only renders if the host has the field
// toggled ON in their intake. Services / Inspo / Products are static placeholders
// that share the canonical aggregation surfaces with Makeup.
const HAIR_INSIGHTS = {
  clientele: [
    { tile: "# clients per allergen",        field_id: "allergens" },
    { tile: "# clients per sensitivity",     field_id: "sensitivities" },
    { tile: "# clients per hair type",       field_id: "hair_type" },
    { tile: "# clients per hair length",     field_id: "hair_length" },
    { tile: "# clients per hair color",      field_id: "hair_color" },
    { tile: "# clients per hair goal",       field_id: "hair_goal" },
    { tile: "# clients per scalp condition", field_id: "scalp_condition" },
    { tile: "# clients per accessibility",   field_id: "accessibility" },
  ],
  services: MAKEUP_INSIGHTS.services,
  inspo:    MAKEUP_INSIGHTS.inspo,
  products: MAKEUP_INSIGHTS.products,
};
const NAILS_INSIGHTS = {
  clientele: [
    { tile: "# clients per allergen",        field_id: "allergens" },
    { tile: "# clients per nail length",     field_id: "nail_length" },
    { tile: "# clients per nail shape",      field_id: "nail_shape" },
    { tile: "# clients per finish",          field_id: "nail_finish" },
    { tile: "# clients per color family",    field_id: "color_family" },
    { tile: "# clients per accessibility",   field_id: "accessibility" },
  ],
  services: MAKEUP_INSIGHTS.services,
  inspo:    MAKEUP_INSIGHTS.inspo,
  products: MAKEUP_INSIGHTS.products,
};
const LASHES_INSIGHTS = {
  clientele: [
    { tile: "# clients per allergen",        field_id: "allergens" },
    { tile: "# clients per lash style",      field_id: "lash_style" },
    { tile: "# clients per lash length",     field_id: "lash_length" },
    { tile: "# clients per lash curl",       field_id: "lash_curl" },
    { tile: "# clients per lash strip brand",field_id: "lash_strip_brand" },
    { tile: "# clients per accessibility",   field_id: "accessibility" },
  ],
  services: MAKEUP_INSIGHTS.services,
  inspo:    MAKEUP_INSIGHTS.inspo,
  products: MAKEUP_INSIGHTS.products,
};
const CULINARY_INSIGHTS = {
  clientele: [
    { tile: "# clients per allergen",        field_id: "allergens" },
    { tile: "# clients per diet",            field_id: "diet" },
    { tile: "# clients per spice level",     field_id: "spice_level" },
    { tile: "# clients per cuisine",         field_id: "cuisine_preferences" },
    { tile: "# clients per accessibility",   field_id: "accessibility" },
  ],
  services: MAKEUP_INSIGHTS.services,
  inspo:    MAKEUP_INSIGHTS.inspo,
  products: MAKEUP_INSIGHTS.products,
};
const WELLNESS_INSIGHTS = {
  clientele: [
    { tile: "# clients per allergen",         field_id: "allergens" },
    { tile: "# clients per wellness goal",    field_id: "wellness_goal" },
    { tile: "# clients per body focus area",  field_id: "body_focus" },
    { tile: "# clients per pressure level",   field_id: "pressure_level" },
    { tile: "# clients per accessibility",    field_id: "accessibility" },
  ],
  services: MAKEUP_INSIGHTS.services, inspo: MAKEUP_INSIGHTS.inspo, products: MAKEUP_INSIGHTS.products,
};
const TRAVEL_INSIGHTS = {
  clientele: [
    { tile: "# clients per travel type",      field_id: "travel_type" },
    { tile: "# clients per destination kind", field_id: "destination_kind" },
    { tile: "# clients per budget tier",      field_id: "budget_tier" },
    { tile: "# clients per group size",       field_id: "group_size" },
    { tile: "# clients per accessibility",    field_id: "accessibility" },
  ],
  services: MAKEUP_INSIGHTS.services, inspo: MAKEUP_INSIGHTS.inspo, products: MAKEUP_INSIGHTS.products,
};
const STYLE_INSIGHTS = {
  clientele: [
    { tile: "# clients per body shape",       field_id: "body_shape" },
    { tile: "# clients per style aesthetic",  field_id: "style_aesthetic" },
    { tile: "# clients per color season",     field_id: "color_season" },
    { tile: "# clients per fit preference",   field_id: "fit_preference" },
    { tile: "# clients per accessibility",    field_id: "accessibility" },
  ],
  services: MAKEUP_INSIGHTS.services, inspo: MAKEUP_INSIGHTS.inspo, products: MAKEUP_INSIGHTS.products,
};
const DISPENSARY_INSIGHTS = {
  clientele: [
    { tile: "# clients per intent",           field_id: "use_intent" },
    { tile: "# clients per dose tolerance",   field_id: "dose_tolerance" },
    { tile: "# clients per format",           field_id: "preferred_format" },
    { tile: "# clients per terpene",          field_id: "terpene_preference" },
    { tile: "# clients per allergen",         field_id: "allergens" },
  ],
  services: MAKEUP_INSIGHTS.services, inspo: MAKEUP_INSIGHTS.inspo, products: MAKEUP_INSIGHTS.products,
};
const OCCASIONS_INSIGHTS = {
  clientele: [
    { tile: "# clients per occasion type",    field_id: "occasion_type" },
    { tile: "# clients per guest count",      field_id: "guest_count" },
    { tile: "# clients per venue type",       field_id: "venue_type" },
    { tile: "# clients per budget tier",      field_id: "budget_tier" },
    { tile: "# clients per accessibility",    field_id: "accessibility" },
  ],
  services: MAKEUP_INSIGHTS.services, inspo: MAKEUP_INSIGHTS.inspo, products: MAKEUP_INSIGHTS.products,
};
const INSTRUCTORS_INSIGHTS = {
  clientele: [
    { tile: "# students per skill level",     field_id: "skill_level" },
    { tile: "# students per learning goal",   field_id: "learning_goal" },
    { tile: "# students per format",          field_id: "session_format" },
    { tile: "# students per group size",      field_id: "group_size" },
    { tile: "# students per accessibility",   field_id: "accessibility" },
  ],
  services: MAKEUP_INSIGHTS.services, inspo: MAKEUP_INSIGHTS.inspo, products: MAKEUP_INSIGHTS.products,
};

const INSIGHT_CATALOGS = {
  makeup:      MAKEUP_INSIGHTS,
  hair:        HAIR_INSIGHTS,
  nails:       NAILS_INSIGHTS,
  lashes:      LASHES_INSIGHTS,
  culinary:    CULINARY_INSIGHTS,
  wellness:    WELLNESS_INSIGHTS,
  travel:      TRAVEL_INSIGHTS,
  style:       STYLE_INSIGHTS,
  dispensary:  DISPENSARY_INSIGHTS,
  occasions:   OCCASIONS_INSIGHTS,
  events:      OCCASIONS_INSIGHTS, // alias for legacy cat_events
  instructors: INSTRUCTORS_INSIGHTS,
};

function CategoryInsightsCatalog({ category, currentTemplate, preferenceFields = [], categoryLightHex = "#f5f5f4" }) {
  const catalog = INSIGHT_CATALOGS[category];
  if (!catalog) {
    return (
      <div className="text-center py-8 text-stone-400 text-sm border-2 border-dashed border-stone-200 rounded-lg" data-testid={`tpl-insights-stub-${category}`}>
        <p>{category} Insights catalog · coming soon</p>
        <p className="text-xs mt-1">Same 4-pillar structure as Makeup (Clientele · Services · Inspo · Products). Each tile binds to a preference field_id and hides if the field isn't toggled in Intake.</p>
      </div>
    );
  }

  // Visibility map - read from currentTemplate's intake settings.
  // Field is visible if its enabled !== false on the field doc AND host hasn't overridden to off.
  const intakeFlags = currentTemplate?.intake_fields || {};
  const fieldsByKey = {};
  preferenceFields.forEach(f => {
    const key = f.key || f.field_id || f.id;
    if (key) fieldsByKey[key] = f;
  });
  const isFieldCollected = (fid) => {
    const liveField = fieldsByKey[fid];
    if (liveField && liveField.enabled === false) return false;
    return intakeFlags[fid]?.collected !== false;
  };

  const PILLARS = [
    { key: "clientele", label: "Clientele at a glance", tiles: catalog.clientele, fieldBound: true },
    { key: "services",  label: "Services",              tiles: catalog.services,  fieldBound: false },
    { key: "inspo",     label: "Inspo",                 tiles: catalog.inspo,     fieldBound: false },
    { key: "products",  label: "Products",              tiles: catalog.products,  fieldBound: false },
  ];

  return (
    <div className="space-y-4" data-testid={`tpl-insights-${category}-catalog`}>
      {PILLARS.map(p => {
        const visible = p.fieldBound
          ? p.tiles.filter(t => isFieldCollected(t.field_id))
          : p.tiles;
        const hidden = p.fieldBound ? p.tiles.length - visible.length : 0;
        return (
          <div key={p.key} data-testid={`tpl-insights-${p.key}`}>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-bold uppercase tracking-wider text-stone-700">{p.label}</h4>
              <span className="text-[10px] text-stone-400">
                {visible.length} tile{visible.length === 1 ? "" : "s"}
                {hidden > 0 && <span className="text-stone-300"> · {hidden} hidden (intake off)</span>}
              </span>
            </div>
            {visible.length === 0 ? (
              <div className="rounded-md border border-dashed border-stone-200 bg-stone-50 p-4 text-center" data-testid={`tpl-insights-empty-${p.key}`}>
                <p className="text-xs text-stone-400">No tiles visible - toggle preference fields ON in Intake to surface insights.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {visible.map((t, i) => (
                  <InsightTile
                    key={i}
                    pillar={p.key}
                    index={i}
                    tile={t}
                    liveField={t.field_id ? fieldsByKey[t.field_id] : null}
                    categoryLightHex={categoryLightHex}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function InsightTile({ pillar, index, tile, liveField, categoryLightHex }) {
  // Live option list (with image_url + label) is the SSOT for clientele tiles.
  // Falls back to the static sampleBucketsFor() preview only when the field
  // hasn't been seeded in db.categories.preference_fields yet.
  const liveOptions = liveField?.options?.filter(o => o.enabled !== false) || [];
  const useLive = pillar === "clientele" && liveOptions.length > 0;
  const fallback = pillar === "clientele" && !useLive ? sampleBucketsFor(tile.field_id) : null;

  return (
    <div className="rounded-md border border-stone-200 bg-white px-2.5 py-2" data-testid={`tpl-insight-${pillar}-${index}`}>
      <p className="text-[11px] font-medium text-stone-800">{tile.tile}</p>
      {useLive ? (
        <div className="mt-1.5 grid grid-cols-3 gap-1.5">
          {liveOptions.slice(0, 6).map((o, i) => (
            <div key={i} className="flex flex-col items-center gap-0.5" title={o.label}>
              <div
                className="w-full aspect-square rounded-md overflow-hidden border border-stone-100 flex items-center justify-center"
                style={{ backgroundColor: o.swatch || categoryLightHex }}
              >
                {o.image_url ? (
                  <img src={o.image_url} alt={o.label} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-[9px] text-stone-400 truncate px-1">{o.label?.[0] || "·"}</span>
                )}
              </div>
              <div className="w-full flex items-center justify-between text-[9px]">
                <span className="text-stone-600 truncate">{o.label}</span>
                <span className="text-stone-300 font-mono">-</span>
              </div>
            </div>
          ))}
          {liveOptions.length > 6 && (
            <p className="text-[9px] text-stone-400 col-span-3 text-center mt-0.5">+ {liveOptions.length - 6} more</p>
          )}
        </div>
      ) : fallback ? (
        <div className="mt-1 space-y-0.5">
          {fallback.slice(0, 4).map((b, i) => (
            <div key={i} className="flex items-center justify-between text-[10px]">
              <span className="text-stone-600 truncate">{b.label}</span>
              <span className="text-stone-300 font-mono">-</span>
            </div>
          ))}
          {fallback.length > 4 && <p className="text-[9px] text-stone-300">+ {fallback.length - 4} more</p>}
        </div>
      ) : (
        <>
          <p className="text-base font-bold text-stone-300 mt-0.5">-</p>
          {tile.source && <p className="text-[9px] text-stone-400 font-mono mt-1">{tile.source}</p>}
        </>
      )}
      <button
        type="button"
        className="mt-1.5 text-[10px] text-purple-600 hover:text-purple-800 underline-offset-2 hover:underline"
        data-testid={`tpl-insight-drilldown-${pillar}-${index}`}
      >
        View clients
      </button>
    </div>
  );
}

// SampleKanbanCard - visual mirror of the canonical preference card the host
// sees on each Kanban card. Layout (top → bottom):
//   1) Inspo strip (3-cell collage from the client's intake inspo - placeholders use category light hex)
//   2) Avatar · client name · service title · gear (settings) icon
//   3) Tag row - synced from the host-toggled preferences (universal allergies/accessibility + category-specific). Hidden if host doesn't toggle.
//   4) Date row - calendar icon + appointment date
function SampleKanbanCard({ laneId, client, categoryLightHex, enabledTagFields }) {
  const [pop, setPop] = useState(false);
  const lightBg = categoryLightHex || "#f5f5f4";
  // Sample inspo cells - empty for the template preview; placeholders show category light hex.
  const sampleInspo = [null, null, null];
  // Tags = host's toggled preferences (universal + category). Empty list if none toggled.
  const tagLabels = (enabledTagFields || []).slice(0, 4).map(f => f.label);
  const sampleService = "Bridal Glam";
  const sampleDate = "Apr 11";
  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid={`tpl-kanban-card-${laneId}`}>
      {/* 1) Inspo strip - placeholder background = category light hex */}
      <div className="grid grid-cols-3 gap-px bg-stone-100" data-testid={`tpl-kanban-inspo-${laneId}`}>
        {sampleInspo.map((src, i) => (
          <div
            key={i}
            className="aspect-[4/5] overflow-hidden"
            style={{ backgroundColor: lightBg }}
          >
            {src && <img src={src} alt="inspo" className="w-full h-full object-cover" />}
          </div>
        ))}
      </div>
      {/* 2) Avatar · name · service · gear */}
      <div className="p-3 flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-stone-900 text-white flex items-center justify-center text-xs font-semibold shrink-0">
          {client.name?.[0] || "S"}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-stone-900 truncate">{client.name}</p>
          <p className="text-[11px] text-stone-400 truncate">{sampleService}</p>
        </div>
        <button
          type="button"
          onClick={() => setPop(true)}
          className="p-1 rounded hover:bg-stone-100 shrink-0"
          title="Open preference card"
          data-testid={`tpl-kanban-eye-${laneId}`}
        >
          <Eye className="w-3.5 h-3.5 text-stone-500" />
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="p-1 rounded hover:bg-stone-100 shrink-0"
              title="State actions"
              data-testid={`tpl-kanban-actions-${laneId}`}
            >
              <Settings className="w-3.5 h-3.5 text-stone-500" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-stone-500">State actions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid={`tpl-action-change-status-${laneId}`}>
              <ListChecks className="w-3.5 h-3.5 mr-2" /> Change status
            </DropdownMenuItem>
            <DropdownMenuItem data-testid={`tpl-action-complete-${laneId}`}>
              <CheckCircle2 className="w-3.5 h-3.5 mr-2" /> Complete appointment
            </DropdownMenuItem>
            <DropdownMenuItem data-testid={`tpl-action-reschedule-${laneId}`}>
              <CalendarDays className="w-3.5 h-3.5 mr-2" /> Reschedule
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid={`tpl-action-archive-${laneId}`} className="text-red-600 focus:text-red-700">
              <ArchiveIcon className="w-3.5 h-3.5 mr-2" /> Cancel / archive
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {/* 3) Tag row - synced from host's toggled preferences */}
      {tagLabels.length > 0 && (
        <div className="px-3 pb-2 flex flex-wrap gap-1.5" data-testid={`tpl-kanban-tags-${laneId}`}>
          {tagLabels.map((t, i) => (
            <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-stone-100 text-stone-700">{t}</span>
          ))}
        </div>
      )}
      {/* 4) Date row */}
      <div className="px-3 pb-3 pt-2 border-t border-stone-100 flex items-center gap-1.5 text-[11px] text-stone-500" data-testid={`tpl-kanban-date-${laneId}`}>
        <CalendarDays className="w-3.5 h-3.5" />
        <span>{sampleDate}</span>
      </div>

      {/* Universal preference card pop-up - opens the SAME visual at full size */}
      <Dialog open={pop} onOpenChange={setPop}>
        <DialogContent className="max-w-sm p-0 overflow-hidden" data-testid={`tpl-pref-card-popup-${laneId}`}>
          <div className="grid grid-cols-3 gap-px bg-stone-100">
            {sampleInspo.map((src, i) => (
              <div key={i} className="aspect-[4/5] overflow-hidden" style={{ backgroundColor: lightBg }}>
                {src && <img src={src} alt="inspo" className="w-full h-full object-cover" />}
              </div>
            ))}
          </div>
          <div className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-stone-900 text-white flex items-center justify-center text-sm font-semibold shrink-0">
              {client.name?.[0] || "S"}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-stone-900 truncate">{client.name}</p>
              <p className="text-xs text-stone-400 truncate">{sampleService}</p>
            </div>
            <Settings className="w-4 h-4 text-stone-400" />
          </div>
          {tagLabels.length > 0 && (
            <div className="px-4 pb-3 flex flex-wrap gap-1.5">
              {tagLabels.map((t, i) => (
                <span key={i} className="text-[11px] px-2.5 py-0.5 rounded-full bg-stone-100 text-stone-700">{t}</span>
              ))}
            </div>
          )}
          <div className="px-4 pb-4 pt-3 border-t border-stone-100 flex items-center gap-1.5 text-xs text-stone-500">
            <CalendarDays className="w-3.5 h-3.5" />
            <span>{sampleDate}</span>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Tiny per-field option-bucket sampler. Real values come from
// `db.categories.preference_fields[$field_id].options` once the backend
// aggregation endpoint lands (next batch).
function sampleBucketsFor(fieldId) {
  const samples = {
    allergens:           [{ label: "Nuts" }, { label: "Dairy" }, { label: "Latex" }, { label: "None" }],
    sensitivities:       [{ label: "Fragrance" }, { label: "Sulfates" }, { label: "Parabens" }],
    skin_type_undertone: [{ label: "Oily · cool" }, { label: "Combo · neutral" }, { label: "Dry · warm" }, { label: "Sensitive · neutral" }],
    foundation_shade:    [{ label: "Light · NC15" }, { label: "Medium · NC30" }, { label: "Tan · NC40" }, { label: "Deep · NC50" }],
    skin_tone_metal:     [{ label: "Gold" }, { label: "Silver" }, { label: "Mixed" }],
    eye_shape:           [{ label: "Almond" }, { label: "Hooded" }, { label: "Monolid" }, { label: "Round" }],
    lash_style:          [{ label: "Natural" }, { label: "Volume" }, { label: "Wispy" }, { label: "Doll" }],
    lash_strip_brand:    [{ label: "Ardell" }, { label: "Lilly Lashes" }, { label: "Velour" }],
    lip_shape:           [{ label: "Full" }, { label: "Heart" }, { label: "Wide" }, { label: "Thin" }],
    lip_finish:          [{ label: "Matte" }, { label: "Satin" }, { label: "Gloss" }, { label: "Stain" }],
    skin_concerns:       [{ label: "Acne" }, { label: "Hyperpigmentation" }, { label: "Fine lines" }, { label: "Dryness" }],
    brand_affinity:      [{ label: "MAC" }, { label: "Charlotte Tilbury" }, { label: "Fenty" }, { label: "Rare" }],
  };
  return samples[fieldId] || [{ label: "-" }];
}

